<!-- The planner. THIS FILE IS YOURS.

     One screen, full bleed. A kitchen is a picture and the picture is the product, so the
     canvas gets the whole page and everything else is a sheet over it.

     The three-dimensional work is deliberately NOT in this file. `src/kitchen/` is plain
     three.js with no Vue in it at all, because the interaction is the part most worth
     testing and least worth debugging through a framework's reactivity. Vue owns the sheet,
     the pickers and the words; three.js owns the room.

     Two sheets, never both: the FINISH sheet is what the whole kitchen wears, and the
     CABINET sheet is what one of them is. Tapping a cabinet opens the second over the first
     and tapping the floor closes it, so there is never a question about what a control will
     change. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="planner">
      <canvas ref="canvas" class="stage" />

      <!-- 3D judges the finish; plan moves the cabinets. One view could never do both, and
           a corner in perspective is the reason nobody could drag round one. -->
      <PlanView v-if="view === 'plan'" :plan="plan" :maker="maker" :kitchen="kitchen"
                :selected="selected" :nudge="nudge" :inset="covered" :top="topH"
                :picking="pickIds"
                @change="nudge++" @select="onSelect" @remember="rememberFor"
                @committed="committed" @hint="planHint" @toggle-wall="toggleWall"
                @pick="togglePick" />

      <header class="brandbar" ref="brandEl">
        <span class="mark">{{ maker.name }}</span>
        <nav class="view" aria-label="View">
          <button type="button" :class="{ on: view === '3d' }" @click="goView('3d')">3D</button>
          <button type="button" :class="{ on: view === 'plan' }" @click="goView('plan')">Plan</button>
        </nav>
        <span class="ghosts">
          <button class="ghost" type="button" @click="undo" :disabled="!history.length" aria-label="Undo">
            <IonIcon :icon="arrowUndoOutline" aria-hidden="true" />
          </button>
          <button class="ghost" type="button" @click="redo" :disabled="!future.length" aria-label="Redo">
            <IonIcon :icon="arrowRedoOutline" aria-hidden="true" />
          </button>
          <button class="ghost" type="button" @click="kitchens = true" aria-label="My kitchens">
            <IonIcon :icon="folderOutline" aria-hidden="true" />
          </button>
        </span>
      </header>

      <!-- ── NOW, OR NEW ──────────────────────────────────────────────────────────────
           A design drawn from a scan can always show the room it came from. Same control
           as 3D/Plan, one row below it, and only there when there IS an Original to show:
           on every kitchen that was typed rather than scanned this row does not exist.

           `Now` swaps the SCENE, never the plan. What is on screen is a throwaway copy of
           the Original, every edit path is shut while it is up, and the note says so --
           because a view you can turn things in looks exactly like a view you can change
           things in, and the whole worth of the Original is that nobody has changed it. -->
      <div v-if="original" class="asscanned" :style="{ top: `${topH + 4}px` }">
        <nav class="view" aria-label="Which kitchen to show">
          <button type="button" :class="{ on: viewing === 'now' }" @click="showOriginal">Now</button>
          <button type="button" :class="{ on: viewing === 'new' }" @click="showDesign">New</button>
        </nav>
        <template v-if="viewing === 'now'">
          <p class="note">This is the room as scanned. Switch back to New to change anything.</p>
          <button v-if="scanFile" type="button" class="file" @click="shareScanModel">
            View the scan file
          </button>
          <button v-else-if="sharedRoomUrl" type="button" class="file" @click="openRoomUrl">
            View the 3D scan
          </button>
        </template>
      </div>

      <!-- The words stay while the pill fades; a pill that empties as it goes shrinks to a dot. -->
      <!-- THE TWO THINGS YOU DO WITH A FINISHED KITCHEN, on the kitchen itself.
           They were at the bottom of the Review sheet, which is three taps and a scroll
           away from the thing they act on. Standing them in the corner of the model puts
           them where the model is. -->
      <div class="rail" :style="{ top: `${topH + 6}px` }">
        <button v-if="canAr && viewing === 'new'" type="button" :disabled="busyRail === 'ar'"
                @click="showInRoom" aria-label="See it in your room in AR">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.5 16.5 6v8L10 17.5 3.5 14V6Z"/><path d="M3.5 6 10 9.5 16.5 6"/><path d="M10 9.5v8"/></svg>
          <span>{{ busyRail === 'ar' ? '…' : 'AR' }}</span>
        </button>
        <!-- Hidden while the room as scanned is up: a link shares the DESIGN, and offering
             it over a picture of the old kitchen is offering the wrong thing. -->
        <button v-if="viewing === 'new'" type="button" @click="shareSheet = true" aria-label="Share this kitchen">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8.5 11.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5l-1 1"/><path d="M11.5 8.5a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5l1-1"/></svg>
          <span>Share</span>
        </button>
        <!-- Icon only, and the smallest thing on the rail: two fingers pan as well as zoom
             now, and a view that can be moved anywhere needs one way back. It is not a
             decision about the kitchen, so it does not get a word. -->
        <button v-if="view === '3d'" type="button" class="icon" @click="recentre"
                aria-label="Centre the view again" title="Centre the view again">
          <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10" cy="10" r="3"/><path d="M10 2v2.5"/><path d="M10 15.5V18"/><path d="M2 10h2.5"/><path d="M15.5 10H18"/></svg>
        </button>
      </div>

      <div class="hint" :class="{ on: hint }" aria-live="polite">{{ hint ?? shownHint }}</div>
      <div class="hint warn" :class="{ on: refusal }" aria-live="assertive">{{ refusal ?? shownRefusal }}</div>

      <!-- ── one cabinet ─────────────────────────────────────────────────────────── -->
      <section v-if="selected" ref="sheetEl" class="sheet cabinet"
               :class="{ sliding: sliding }" :style="sheetStyle">
        <div class="grab" @pointerdown="grabSheet" @pointermove="dragSheet"
             @pointerup="dropSheet" @pointercancel="dropSheet"
             role="button" tabindex="0" :aria-expanded="!down"
             aria-label="Drag down to see the kitchen" @keydown.enter.prevent="toggleSheet"
             @keydown.space.prevent="toggleSheet"><span /></div>
        <header class="who">
          <div>
            <h2>{{ selectedSpec?.name ?? 'Filler' }}</h2>
            <p>{{ selected.w }}mm wide<span v-if="selectedSpec">, {{ selectedSpec.h }} tall,
              {{ selectedSpec.d }} deep</span><span v-if="selected.stretched">, made to fit</span></p>
          </div>
          <div class="acts">
            <!-- Removing is the destructive one, so it is an icon with a label read out
                 rather than a full-width button at the bottom of a sheet you have to scroll
                 to reach. It sits beside Done because that is where a person looks when
                 they have finished with a cabinet -- one way or the other. -->
            <button class="del" type="button" @click="remove"
                    aria-label="Take this one out" title="Take this one out">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6h12"/><path d="M8 6V4.5h4V6"/><path d="M5.5 6l.7 9.5A1.5 1.5 0 0 0 7.7 17h4.6a1.5 1.5 0 0 0 1.5-1.5L14.5 6"/><path d="M8.5 9v5"/><path d="M11.5 9v5"/></svg>
            </button>
            <button class="ghost" type="button" @click="close">Done</button>
          </div>
        </header>

        <p class="label">Change it to</p>
        <div class="swatches">
          <button v-for="c in swappable" :key="c.id" type="button"
                  class="cab" :class="{ on: c.id === selected.specId }"
                  @click="swap(c.id)">
            <span class="glyph" v-html="glyph(c)" />
            <span class="name">{{ c.name }}</span>
            <span class="dims">{{ c.w }} &times; {{ c.h }}</span>
          </button>
        </div>

        <p class="label" v-if="canResize">Width</p>
        <div class="width" v-if="canResize">
          <input type="range" :min="selectedSpec.stretch[0]" :max="widthCeiling"
                 :value="selected.w" step="1" @input="setWidth($event.target.valueAsNumber)" />
          <span class="mm">{{ selected.w }}<i>mm</i></span>
        </div>

        <!-- The things that cannot move: where the plumbing is, where the gas is. Locked, a
             cabinet stays put through a drag, a resize and a redesign. -->
        <button v-if="lockable" class="lock" :class="{ on: isLocked }" type="button" @click="toggleLock">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="9" width="12" height="9" rx="2"/><path :d="isLocked ? 'M7 9V6a3 3 0 0 1 6 0v3' : 'M7 9V6a3 3 0 0 1 5.6-1.5'"/></svg>
          {{ isLocked ? 'Kept here — tap to let it move' : 'Keep this here' }}
        </button>


      </section>

      <!-- ── the whole kitchen ───────────────────────────────────────────────────── -->
      <!-- ── the whole kitchen: one decision at a time, given room ──────────────────
           Three kinds of decision, not eight peer tabs. What the room IS, what is IN it,
           and what it WEARS -- and a fourth thing that is not a decision, which is looking
           at the whole and sending it. Each gets the sheet to itself. -->
      <!-- `mode-finish`, not `finish`: the panel inside is `.finish`, and a bare mode class
           here made every panel rule style the whole SHEET as well -- the finish list's card
           (inset margins, a border, a grey ground, rounded corners) was being applied to the
           sheet itself, which is why it stopped matching the others. -->
      <section v-else ref="sheetEl" class="sheet"
               :class="[`mode-${mode}`, { sliding, guided: !!flowCard }]"
               :style="sheetStyle">
        <div class="grab" @pointerdown="grabSheet" @pointermove="dragSheet"
             @pointerup="dropSheet" @pointercancel="dropSheet"
             role="button" tabindex="0" :aria-expanded="!down"
             aria-label="Drag down to see the kitchen" @keydown.enter.prevent="toggleSheet"
             @keydown.space.prevent="toggleSheet"><span /></div>

        <!-- ── WHERE YOU ARE ────────────────────────────────────────────────────────
             Four words at the top of the sheet, the one you are on in black.

             The tab bar says which TAB is open; it has never said how far through this you
             are, and a person who has just scanned their kitchen has no way of knowing that
             three more things happen before there is anything to send anybody. The owner's
             complaint was "having to change tabs so many times back and forth" -- half of
             which is not the tab changes, it is not knowing how many are left.

             Only while a fresh scan or a fresh start is being walked through. A design
             opened from My kitchens or a link is not a journey with an end, and a progress
             line over one is a progress line that never completes. -->
        <ol v-if="flow" class="steps" aria-label="Where you are">
          <li v-for="s in STEPS" :key="s.n"
              :class="{ on: flowStep === s.n, done: flowStep > s.n }">
            <button type="button" :disabled="!stepReady(s.n)" @click="goStep(s.n)">
              <i>{{ s.n }}</i>{{ s.label }}
            </button>
          </li>
        </ol>

        <!-- ROOM -->
        <div v-if="mode === 'room'" class="room">
          <!-- ── THE GUIDED FLOW ────────────────────────────────────────────────────
               One card, one primary button, at the top of the panel. It is the whole of
               steps 1 and 2 for somebody who has just opened the app or just finished a
               scan, and it replaces the found-kitchen card that used to sit halfway down
               here under everything it was more important than.

               Every consequence is still decided in this file -- ScanFlow.vue shouts and
               this page acts, which is why the state machine reads in one place. -->
          <ScanFlow v-if="flowCard" :stage="flowCard" :can-scan="scan.ok" :scan-why="scanWhy"
                    :busy="busy" :found="scanned_" :found-line="foundLine" :saw-line="sawLine"
                    :left-over-line="leftOverLine"
                    :service-line="serviceLine" :locks="scanLocks" :service-word="SERVICE_WORD"
                    :keep-island="keepIslandInDesign" :has-island="!!original?.islands?.length"
                    :photo-walls="photoWalls" :can-look="!!original" :has-scan-file="!!scanFile"
                    :problem-kind="scanProblem?.kind ?? null" :problem-detail="scanProblem?.detail ?? null"
                    :typed="typedWalls" :has-raw-file="!!(scanModel && scanProblem?.scanned)"
                    :pick-line="pickLine"
                    @scan="scanRoom" @type-walls="typeTheWalls" @design="designFromTheScan"
                    @keep-layout="designFromScanLayout" @photograph="photographWall"
                    @look="showOriginal" @copy="copyScan" @share="shareScanModel"
                    @open-scan-file="shareRawScan"
                    @pick-all="pickAll" @pick-none="pickNone"
                    @back="flow = 'scanned'" @toggle-lock="(id, on) => { scanLocks[id] = on; }"
                    @update:keep-island="(v) => { keepIslandInDesign = v; }" />

          <!-- MEASURING THE ROOM IS THE FIRST JOB, and on the right phone the fastest way to
               do it is to walk around pointing the camera at the walls. That used to be a
               quiet grey line below the number fields, on the phones that had it and nowhere
               else -- so on every other device the feature left no trace and read as
               unbuilt. It leads the panel now, and where it cannot run it says which of the
               reasons it is rather than vanishing.

               This is the version for somebody who is NOT being walked through: a design
               opened from My kitchens, or one already being edited. During the guided flow
               the hero above is the same offer, said better. -->
          <template v-if="!flowCard">
          <p class="label">Measure the room</p>
          <button v-if="scan.ok" class="scan" type="button" @click="scanRoom" :disabled="busy">
            <span class="glyph">
              <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7V4.5A1.5 1.5 0 0 1 4.5 3H7"/><path d="M13 3h2.5A1.5 1.5 0 0 1 17 4.5V7"/><path d="M17 13v2.5a1.5 1.5 0 0 1-1.5 1.5H13"/><path d="M7 17H4.5A1.5 1.5 0 0 1 3 15.5V13"/><circle cx="10" cy="10" r="2.6"/></svg>
            </span>
            <span class="text">
              <span class="k">{{ busy ? 'Scanning…' : 'Scan the room' }}</span>
              <span class="b">Walk around pointing the phone at the walls. They measure themselves.</span>
            </span>
            <svg class="go" width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 4l6 6-6 6"/></svg>
          </button>
          <p v-else class="noscan">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7V4.5A1.5 1.5 0 0 1 4.5 3H7"/><path d="M13 3h2.5A1.5 1.5 0 0 1 17 4.5V7"/><path d="M17 13v2.5a1.5 1.5 0 0 1-1.5 1.5H13"/><path d="M7 17H4.5A1.5 1.5 0 0 1 3 15.5V13"/><circle cx="10" cy="10" r="2.6"/></svg>
            <span><b>Scan the room</b> — {{ scanWhy }}</span>
          </p>
          </template>

          <!-- ── WHAT THE ROOM IS, TYPED ────────────────────────────────────────────
               Shape, island and wall lengths: everything that was always here, unmoved.

               Hidden only for the two moments where showing it is actively harmful. On the
               Start card it is the ALTERNATIVE to scanning and reveals itself when asked
               for -- a hero button competing with a grid of shape presets is not a hero
               button. Right after a scan it is worse than clutter: `setShape` REPLACES the
               room, walls, doorways and all, and the presets sat two finger-widths under
               the found-kitchen card. -->
          <template v-if="showRoomFields">

          <p class="label">Shape</p>
          <div class="shapes">
            <button v-for="sh in SHAPES" :key="sh.id" type="button"
                    :class="{ on: plan.shape === sh.id }" @click="setShape(sh.id)">
              <span class="glyph" v-html="sh.glyph" />
              <span class="name">{{ sh.name }}</span>
            </button>
          </div>

          <!-- THE ISLAND, under the shape it belongs beside.
               It was a text link at the bottom of the sheet, below the pictures and above
               "any other shape", where nobody found it: adding an island is a decision about
               the SHAPE of the kitchen, so it sits with the shapes. -->
          <!-- Hidden at the very start, and only there: on a device that cannot scan, the
               Start step has to fit the shape, the wall fields AND the button that designs
               from them above the fold of a 405px sheet, and "Add an island" is 60px of a
               decision nobody makes before they have a kitchen. It comes back the moment
               there is one to change. -->
          <button v-if="!plan.islands?.length && flowCard !== 'start'" class="island-add"
                  type="button" @click="addIsland">
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2.5" y="7" width="15" height="6" rx="1"/><path d="M10 4v3"/></svg>
            Add an island
          </button>
          <!-- Explicitly on "there is one", not `v-else`: the button above hides itself on
               the Start step, and an `v-else` there handed the island PANEL to a plan with
               no island in it -- a Length of 0mm and a Take it out that took nothing out. -->
          <div v-else-if="plan.islands?.length" class="isle">
            <div class="head">
              <p class="k">Island</p>
              <button type="button" class="quiet warn" @click="dropIsland">Take it out</button>
            </div>
            <div class="fields">
              <label>
                <span class="which">Length</span>
                <span class="field">
                  <input type="number" inputmode="numeric" min="900" max="4800" step="50"
                         :value="islandLength" @change="setIslandLength($event.target.valueAsNumber)" />
                  <i>mm</i>
                </span>
              </label>
              <label>
                <span class="which">Seating overhang</span>
                <span class="field">
                  <input type="number" inputmode="numeric" min="0" max="600" step="25"
                         :value="islandOverhang" @change="setIslandOverhang($event.target.valueAsNumber)" />
                  <i>mm</i>
                </span>
              </label>
            </div>
            <div class="spin">
              <span class="which">Angle</span>
              <button type="button" aria-label="Turn 15 degrees anticlockwise" @click="spinIsland(-15)">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10a6 6 0 1 1 2 4.5"/><path d="M4 6v4h4"/></svg>
              </button>
              <span class="deg">{{ islandAngle }}&deg;</span>
              <button type="button" aria-label="Turn 15 degrees clockwise" @click="spinIsland(15)">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 10a6 6 0 1 0-2 4.5"/><path d="M16 6v4h-4"/></svg>
              </button>
              <button type="button" class="quiet flip" @click="flipIsland">Turn it round</button>
            </div>
            <p class="b">Drag it on the plan to move it. The seating side is the dashed one.</p>
          </div>

          <p class="label">Or type it, wall to wall</p>
          <div class="walls">
            <label v-for="w in wallFields" :key="w.id">
              <span class="tag">{{ w.tag }}</span>
              <span class="which">{{ w.name }}</span>
              <span class="field">
                <input type="number" inputmode="numeric" :min="ROOM.min" :max="ROOM.max"
                       step="10" :value="w.mm"
                       @change="setWall(w.id, $event.target.valueAsNumber)" />
                <i>mm</i>
              </span>
            </label>
          </div>
          <!-- The one button that matters most sits where a thumb lands, not under the pictures. -->
          <button class="design" type="button" @click="designForMe()">
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.5l1.6 4.2 4.4.4-3.4 2.9 1.1 4.3L10 12l-3.7 2.3 1.1-4.3L4 7.1l4.4-.4Z"/></svg>
            Design it for me
          </button>

          <!-- Pictures they bring: the builder's plan, the room as it is, a render they made
               elsewhere. Theirs, kept with the design, sent to the maker -- and a plan is read
               for the walls when the server can read one. Camera, gallery or a file: the
               phone's own picker offers all three. -->
          <p class="label">Or start from a picture</p>
          <div class="addimg">
            <button v-for="k in IMAGE_KINDS" :key="k.id" type="button" @click="addImage(k.id)" :disabled="busy">
              <span class="glyph" v-html="PICTURE_GLYPH[k.id]" />
              <span class="k">{{ busy ? 'Reading…' : PICTURE_SHORT[k.id] }}</span>
            </button>
          </div>
          <p class="fine picnote">
            <template v-if="features.readImage">
              A plan or a photo of the room is read for the walls, then designed. A kitchen you
              like is matched to {{ maker.name }}'s doors, colours and benchtops.
            </template>
            <template v-else>
              Kept with the design for {{ maker.name }} to see. Reading them to design from is
              coming.
            </template>
          </p>
          <div class="images" v-if="plan.images?.length">
            <figure v-for="im in plan.images" :key="im.id">
              <img :src="im.dataUrl" :alt="kindName(im.kind)" @click="showImage(im)" />
              <figcaption>{{ kindName(im.kind) }}<button type="button" @click="dropImage(im)" aria-label="Remove">×</button></figcaption>
            </figure>
          </div>
          <div v-if="readRoom" class="read">
            <p class="label">What the plan says</p>
            <p class="fine" v-if="readRoom.notes?.length">{{ readRoom.notes.join(' ') }}</p>
            <p class="fine" v-if="readRoom.fixtures?.length">
              Already there: {{ readRoom.fixtures.map((f) => `${f.role} on the ${f.label ?? f.wall} wall`).join(', ') }}.
            </p>
            <p class="fine" v-if="readRoom.confidence < 0.5">The reader was not sure. Check every number against a tape.</p>
            <div class="walls">
              <label v-for="(w, i) in readRoom.walls" :key="i">
                <span class="tag">{{ String.fromCharCode(65 + i) }}</span>
                <span class="which">{{ w.label }}</span>
                <span class="field">
                  <input type="number" inputmode="numeric" step="10" v-model.number="w.mm" />
                  <i>mm</i>
                </span>
              </label>
            </div>
            <div class="readrow">
              <button type="button" class="design" @click="applyRead">Use these walls, then design</button>
              <button type="button" class="quiet" @click="readRoom = null">Not this</button>
            </div>
          </div>

          <button class="quiet" type="button" @click="view = 'plan'">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3h14v14H3Z"/><path d="M3 8h6V3"/><path d="M9 8h8"/><path d="M9 8v9"/></svg>
            Any other shape: drag the walls on the plan
          </button>
          </template>
        </div>

        <!-- CABINETS: the whole range, by kind -->
        <div v-else-if="mode === 'cabinets'" class="cabinets">
          <nav class="kinds" aria-label="Kind">
            <button v-for="k in KINDS" :key="k.id" type="button"
                    :class="{ on: kind === k.id }" @click="kind = k.id">{{ k.label }}</button>
            <span class="count">{{ cabs.length }} in range</span>
          </nav>
          <div class="grid">
            <button v-for="c in cabs" :key="c.id" type="button" class="cab" @click="add(c.id)">
              <span class="glyph" v-html="glyph(c)" />
              <span class="name">{{ c.name }}</span>
              <span class="dims">{{ c.w }} &times; {{ c.h }}</span>
            </button>
          </div>
          <p class="fine">
            Tap one and it goes on the {{ kindWord }} run; the neighbours make room, never
            past what {{ maker.name }} will cut. Then drag it where you want it.
          </p>
          <!-- The way on, said out loud. Somebody who has just picked a kitchen has no
               reason to know that the finishes are a tab away rather than the end. -->
          <button v-if="flow" class="next" type="button" @click="go('finish')">
            Next: choose finishes
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 4l6 6-6 6"/></svg>
          </button>
        </div>

        <!-- FINISH: every choice visible at once, with what you picked spelled out -->
        <div v-else-if="mode === 'finish'" class="finish">
          <div v-for="f in finishRows" :key="f.id" class="row" :class="{ open: finishOpen === f.id }">
            <button type="button" class="head" @click="finishOpen = finishOpen === f.id ? null : f.id">
              <span class="chip" :style="chipFor(f)"><span v-if="f.id === 'doorStyleId'" class="glyph" v-html="styleGlyph(current(f))" /><span v-else-if="f.id === 'handleId'" class="glyph" v-html="handleGlyph(current(f))" /><span v-else-if="f.needs" class="glyph fit" v-html="fittingGlyph(f, current(f))" /></span>
              <span class="text">
                <span class="k">{{ f.label }}</span>
                <span class="v">{{ current(f).name }}</span>
              </span>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l5 5 5-5"/></svg>
            </button>
            <div v-if="finishOpen === f.id" class="swatches">
              <button v-for="o in maker[f.list]" :key="o.id" type="button"
                      class="swatch" :class="{ on: plan[f.id] === o.id }" @click="pick(f.id, o.id)">
                <span class="chip" :style="optionChip(f, o)">
                  <span v-if="f.id === 'doorStyleId'" class="glyph" v-html="styleGlyph(o)" />
                  <span v-else-if="f.id === 'handleId'" class="glyph" v-html="handleGlyph(o)" />
                  <span v-else-if="f.needs" class="glyph fit" v-html="fittingGlyph(f, o)" />
                </span>
                <span class="name">{{ o.name }}</span>
              </button>
            </div>
          </div>
          <p class="fine roomnote">
            The floor and the wall colour are the room, not the kitchen: they are here so you
            can see what the doors look like against them, and {{ maker.name }} does not
            quote or supply either.
          </p>
          <button v-if="flow" class="next" type="button" @click="go('review')">
            Next: see the whole thing
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 4l6 6-6 6"/></svg>
          </button>
        </div>

        <!-- OPTIONS: three kitchens for this room -->
        <Options v-else-if="mode === 'options'" :options="designed.options" :rejected="designed.rejected"
                 :locks="plan.locks" :plan="plan" :maker="maker" :chosen="chosenOption"
                 @choose="chooseOption" @more="designForMe(designSeed + 1)" @next="go('finish')" />

        <!-- ASK: a change, in words -->
        <Ask v-else-if="mode === 'ask'" :plan="plan" :maker="maker" :kitchen="kitchen" :ops="ops" />

        <!-- REVIEW: the whole, and what a designer would say about it -->
        <div v-else class="schedule">
          <p class="lede" v-if="problems.length" role="alert">{{ problems[0] }}</p>
          <template v-else>
            <p class="lede">
              <b>{{ sched.counts.cabinets }} cabinets</b> in
              {{ sched.finishes.doors }}, {{ sched.benchtopMetres }}m of
              {{ sched.finishes.benchtop.toLowerCase() }}<template v-if="est">,
              <b class="est">about {{ est.text }}</b> fitted</template>.
            </p>
            <p class="fine" v-if="!findings.length">
              Every one of them is something {{ maker.name }} builds, and a designer would
              have nothing to add.
            </p>
          </template>

          <!-- Getting it out, first: it is what this tab is for. The documents are the
               way out now -- there is no maker's inbox behind a form, so the button that
               claimed to send one is a share of the whole package instead. -->
          <Exports :plan="plan" :maker="maker" :kitchen="kitchen" :estimate="est" :findings="findings"
                   :must-fix="mustFix.length" :disabled="!!problems.length" :scan-model="scanFile"
                   @said="say" @drawings="drawings = true" />

          <ul class="findings" v-if="findings.length">
            <li v-for="f in findings" :key="f.code + (f.itemId ?? '')"
                :class="[f.level, { goes: !!f.itemId }]" @click="goTo(f)">
              <span class="tag">{{ f.level === 'fault' ? 'Must fix' : 'Worth knowing' }}</span>
              <span class="what">{{ f.text }}</span>
              <span class="acts">
                <button v-if="fixFor(f, maker)" type="button" class="fix" @click.stop="applyFix(f)">
                  {{ fixFor(f, maker).label }}
                </button>
                <span v-if="f.itemId" class="show">Show me
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 4l6 6-6 6"/></svg>
                </span>
              </span>
            </li>
          </ul>

          <!-- What they brought, so the maker's summary shows it. Adding is in Room. -->
          <div class="images" v-if="plan.images?.length" style="margin-top: 12px;">
            <figure v-for="im in plan.images" :key="im.id">
              <img :src="im.dataUrl" :alt="kindName(im.kind)" @click="showImage(im)" />
              <figcaption>{{ kindName(im.kind) }}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <!-- ── the bar: three decisions, and the way out ───────────────────────────── -->
      <nav class="bar" aria-label="Planner">
        <button type="button" :class="{ on: mode === 'room' && !selected }" @click="go('room')">
          <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 4h14v12H3Z"/><path d="M3 9h5V4"/></svg>
          <span>Room</span>
        </button>
        <button type="button" :class="{ on: mode === 'cabinets' && !selected }" @click="go('cabinets')">
          <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3h14v14H3Z"/><path d="M10 3v14"/><path d="M3 10h14"/></svg>
          <span>Cabinets</span>
        </button>
        <button type="button" :class="{ on: mode === 'finish' && !selected }" @click="go('finish')">
          <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.5c3.6 3.2 5.5 5.9 5.5 8.2a5.5 5.5 0 0 1-11 0c0-2.3 1.9-5 5.5-8.2Z"/></svg>
          <span>Finish</span>
        </button>
        <button type="button" :class="{ on: mode === 'review' && !selected, todo: mustFix.length }" @click="go('review')">
          <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10.5 8 15l9-10"/></svg>
          <span>Review</span>
        </button>
      </nav>

      <!-- A change in words, where the server can hear one. Floats above the sheet so it is
           reachable from every tab without being a fifth one. -->
      <button v-if="features.chat && viewing === 'new'" type="button" class="askbtn" :class="{ on: mode === 'ask' }"
              :style="{ bottom: `${covered + 12}px` }" @click="go('ask')">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 5.5A2.5 2.5 0 0 1 5.5 3h9A2.5 2.5 0 0 1 17 5.5v6a2.5 2.5 0 0 1-2.5 2.5H9l-4 3v-3H5.5A2.5 2.5 0 0 1 3 11.5Z"/></svg>
        Ask
      </button>

      <Drawings v-if="drawings" :plan="plan" :maker="maker" @close="drawings = false" @said="say" />
      <ScanIntro v-if="scanIntro" :maker="maker" @close="scanIntro = false" @start="startScan" />
      <Kitchens v-if="kitchens" :plan="plan" :maker="maker"
                @close="kitchens = false" @open="openKitchen" @fresh="freshKitchen" @said="say"
                @design="designFromSaved" @look="lookAtOriginal"
                @named="(n) => { plan.name = n; nudge++; }" />
      <!-- What goes with the link: the design always, and the room, the photos and the 3D
           scan only when ticked. Anything past the design is a package on the server. -->
      <ShareSheet v-if="shareSheet" :plan="plan" :maker="maker" :original="original" :scan-model="scanFile"
                  @close="shareSheet = false" @said="say" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, shallowRef, nextTick } from 'vue';
import { IonPage, IonContent, IonIcon } from '@ionic/vue';
import { arrowUndoOutline, arrowRedoOutline, folderOutline } from 'ionicons/icons';
import Kitchens from '../components/Kitchens.vue';
import Exports from '../components/Exports.vue';
import ShareSheet from '../components/ShareSheet.vue';
import { openShared, idFromSearch } from '../kitchen/hosted.js';
import { saveCurrent, loadCurrent, decodeShare, codeFromHash,
  saveOriginal, loadOriginal, designFrom, deserialize } from '../kitchen/store.js';
import { estimate } from '../kitchen/price.js';
import { fixFor } from '../kitchen/fixes.js';
import { arSupported, exportUsdz, viewInAr } from '../kitchen/ar.js';
import { shareLink, copyText, shareNativeFile, isNative } from '../kitchen/share.js';
import { prefs } from '../core/storage.js';

import { Kitchen } from '../kitchen/scene.js';
import { attach } from '../kitchen/touch.js';
import { DEMO_MAKER, starterPlan } from '../kitchen/maker.js';
import { applyBrand } from '../kitchen/brand.js';
import { faults } from '../kitchen/layout.js';
import * as room from '../kitchen/room.js';
import { schedule } from '../kitchen/schedule.js';
import { review } from '../kitchen/guidelines.js';
import PlanView from '../components/PlanView.vue';
import Options from '../components/Options.vue';
import Ask from '../components/Ask.vue';
import { slide as slideItems, offsets as itemOffsets } from '../kitchen/layout.js';
import { divideRun, FIXED_ROLES } from '../kitchen/divide.js';
import { designOrFill, LOCKABLE } from '../kitchen/design.js';
import { KINDS as IMAGE_KINDS, pickImage, shrink, attach as attachImage } from '../kitchen/images.js';
import { scanStatus, scan as runScan } from '@harbourside/roomplan';
import { loopFromSegments, scanReason } from '../kitchen/roomplan.js';
import { fromScan, kitchenWalls, roleOf } from '../kitchen/reproduce.js';
import ScanIntro from '../components/ScanIntro.vue';
import ScanFlow from '../components/ScanFlow.vue';
import Drawings from '../components/Drawings.vue';

/**
 * The five things a kitchen wears, as rows.
 *
 * A tab strip showed one at a time and made you remember the rest. A list shows all five with
 * what you picked spelled out beside each, and opens the one you tap. `list` is the maker's
 * field the options come from; `chip` is how the current choice is painted.
 */
const FINISHES = [
  { id: 'doorStyleId', label: 'Door style', list: 'doorStyles' },
  { id: 'colourId', label: 'Colour', list: 'colours' },
  { id: 'benchtopId', label: 'Benchtop', list: 'benchtops' },
  { id: 'splashbackId', label: 'Splashback', list: 'splashbacks' },
  { id: 'handleId', label: 'Handles', list: 'handles' },
  // The room around the kitchen. Not cabinetry, not quoted, and said so under the list --
  // but the floor is the biggest surface in the picture and the one that decides whether a
  // sage door looks right, so choosing it is part of choosing a kitchen.
  /*
   * THE FITTINGS, shown only when the kitchen HAS one.
   *
   * A rangehood style row above a kitchen with no rangehood in it is a question about
   * nothing, and five rows of those is how a picker stops being read. `needs` is the role
   * that has to be standing in the kitchen for the row to appear.
   */
  { id: 'rangehoodId', label: 'Rangehood', list: 'rangehoods', needs: 'rangehood' },
  { id: 'fridgeId', label: 'Fridge', list: 'fridges', needs: 'fridge' },
  { id: 'dishwasherId', label: 'Dishwasher', list: 'dishwashers', needs: 'dishwasher' },
  { id: 'ovenId', label: 'Oven', list: 'ovens', needs: 'oven' },
  // The kick has always been on the plan (`kickColourId`, and it rides the share link) and
  // has never been askable: it defaulted to graphite and stayed there. It is the one band of
  // colour at floor level and it is what a person notices when the floor is dark.
  { id: 'kickColourId', label: 'Kickboard', list: 'colours' },
  { id: 'floorId', label: 'Floor', list: 'floors' },
  { id: 'wallColourId', label: 'Walls', list: 'wallColours' },
];

/*
 * The four chips over the cabinet grid.
 *
 * Three of them are the carcass kinds a maker's range is organised by. The fourth is not a
 * kind at all: it is every cabinet that carries an APPLIANCE, gathered by role. A person
 * looking for somewhere to put a fridge does not think "that will be a tall unit" -- they
 * think fridge, and a fridge space filed under Tall between two pantries is a fridge space
 * nobody finds. The same cabinets still appear under their own kind; this is a second way in.
 */
const APPLIANCE_ROLES = ['fridge', 'oven', 'dishwasher', 'sink', 'cooktop', 'rangehood'];
const KINDS = [
  { id: 'base', label: 'Base', word: 'base' },
  { id: 'wall', label: 'Overhead', word: 'overhead' },
  { id: 'tall', label: 'Tall', word: 'base' },
  { id: 'appliance', label: 'Appliances', word: 'base' },
];

/**
 * What counts as a room.
 *
 * Not taste: under 1.5m there is no wall a base cabinet fits against, and over 9m the tool is
 * being used for something it was not built for and the camera framing stops making sense.
 * The input STOPS at these rather than warning about them.
 */
const ROOM = { min: 1500, max: 9000 };

const canvas = ref(null);
const view = ref('3d');
/*
 * WHICH KITCHEN THE SCENE IS SHOWING: the design being worked on, or the room as scanned.
 *
 * `plan` is always the design. `viewing` only ever changes what three.js has been handed --
 * and what it has been handed while this says 'now' is a THROWAWAY COPY of the Original,
 * made fresh by `deserialize` each time. That is belt as well as braces: `setWalls` writes
 * run.from, run.to and refitted items straight into whatever plan it is given, so handing it
 * the stored record would edit the one thing in this app that must never be edited.
 *
 * Everything that writes to the plan checks `readOnly()` first. See it, below.
 */
const viewing = ref('new');
const maker = DEMO_MAKER;
const plan = ref(starterPlan(maker));
// Opens on Room: the first thing anybody has to tell it is how big the room is, and the
// first thing worth pressing is Design it for me, which is at the bottom of that panel.
const mode = ref('room');

/*
 * ── THE GUIDED FLOW ───────────────────────────────────────────────────────────────────
 *
 * WHY. Every capability in this app worked and the path through them did not. A person
 * scanned their kitchen and got a card halfway down the Room sheet, under a scan button and
 * over a grid of shape presets that would have thrown the scan away; two buttons of equal
 * weight; then a design on a second tab, finishes on a third, review on a fourth. The
 * owner, after using it on his own phone: "the flow for the user through the app to scan
 * and then design is a bit confusing and the buttons are not clear. It should be a much
 * easier, clearer flow without having to change tabs so many times back and forth."
 *
 * So there is a stepper now, and it lives in the Room tab's sheet -- NOT in a fifth tab.
 * A tab is a place you can be; a step is a thing you are part-way through, and the two are
 * different ideas. The tab bar is untouched.
 *
 * `flow` is the stepper's own state, and it is null whenever there is no journey to walk:
 * a design opened from My kitchens or from a link is somebody's finished work, and a
 * progress line over one is a progress line that never completes. It comes back the moment
 * a new scan is run.
 *
 *   'start'    the hero: scan, or type the walls in
 *   'scanned'  what the scan found, and the three things to do about it
 *   'walls'    which of the room's walls the kitchen may stand against -- see below
 *   'problem'  a scan came back and could not be turned into a room -- see `scanFailed`
 *   'photos'   photograph each wall, where the server can read one (optional)
 *   'design'   the three kitchens
 *   'editing'  a kitchen has been chosen; the steps still show the way to Review
 *    null      not a journey -- ordinary editing
 */
const flow = ref('start');
/** Which card the Room panel shows, or null when the flow is past the room. */
const flowCard = computed(() =>
  (['start', 'scanned', 'walls', 'problem', 'photos'].includes(flow.value) ? flow.value : null));
const STEPS = [
  { n: 1, label: 'Scan' },
  { n: 2, label: 'Choose' },
  { n: 3, label: 'Finishes' },
  { n: 4, label: 'Review' },
];
/*
 * Which step is current, read off the TAB rather than stored.
 *
 * Two sources of truth about where somebody is would disagree the first time they tapped a
 * tab directly, and the tab is the one they can see. Cabinets counts as Choose: choosing the
 * cabinets is what that tab is for, and it sits between the three kitchens and the finishes.
 */
const flowStep = computed(() => {
  if (mode.value === 'review') return 4;
  if (mode.value === 'finish') return 3;
  if (mode.value === 'options' || mode.value === 'cabinets') return 2;
  return flow.value === 'editing' ? 2 : 1;
});
/** A step is reachable once there is something on the other side of it to look at. */
function stepReady(n) {
  if (n === 1) return true;
  if (n === 2) return designed.value.options.length > 0 || flow.value === 'editing';
  return flow.value === 'editing';
}
function goStep(n) {
  if (!stepReady(n)) return;
  go(['room', designed.value.options.length ? 'options' : 'cabinets', 'finish', 'review'][n - 1]);
}
/*
 * Whether the shape presets and the wall fields are on show.
 *
 * Always, except for the two moments where they do harm. On the Start card they are the
 * ALTERNATIVE to scanning and wait behind "or type the walls in instead", because a hero
 * button standing next to a grid of shape presets is not a hero button. Straight after a
 * scan they are worse than clutter: `setShape` REPLACES the room the scan just measured,
 * doorways and all, and the presets used to sit two finger-widths below the found card.
 */
const typedWalls = ref(false);
const showRoomFields = computed(() => {
  if (!flowCard.value) return true;
  // On the problem card they are the way FORWARD and nothing else is at risk: a scan that
  // could not be turned into a room never touched the plan, so there is no measured room
  // for `setShape` to replace. They still wait to be asked for, so the card's own advice --
  // scan again, and how -- is read first.
  if (flowCard.value === 'problem') return typedWalls.value;
  return flowCard.value === 'start' && (typedWalls.value || !scan.value.ok);
});
/*
 * The same one line from two cards, and it does the same thing from both.
 *
 * From the problem card it is the way past a scan that would not become a room, and it is
 * only the FIELDS: the scan's objects are millimetre positions in a room that was never
 * built, so there is nothing to hang them on and offering them would be offering a kitchen
 * laid out against walls that do not exist. The card stays above the fields either way, so
 * the report and the 3D file are still one tap from here.
 */
function typeTheWalls() {
  typedWalls.value = true;
  say('Type each wall, then Design it for me.');
}
/** The option taken from the three, so Options can offer the way on rather than repeat itself. */
const chosenOption = ref(null);
const finishOpen = ref(null);
const kind = ref('base');
const hint = ref(null);
const refusal = ref(null);
const history = ref([]);
const future = ref([]);          // what Undo took away, until something new is done
const kitchens = ref(false);
const brandEl = ref(null);
const topH = ref(0);

/** Three pictures, three verbs, short enough to sit in a row. */
const PICTURE_SHORT = { plan: 'Floor plan', photo: 'Photo of the room', render: 'Kitchen I like' };
const PICTURE_GLYPH = {
  plan: '<svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h14v14H3Z"/><path d="M3 9h6V3"/><path d="M9 9h8"/><path d="M13 9v8"/></svg>',
  photo: '<svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h3l1.5-2h5l1.5 2h3v9H3Z"/><circle cx="10" cy="11" r="2.8"/></svg>',
  render: '<svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 4h14v12H3Z"/><path d="M3 13l4-4 3 3 2-2 5 5"/><circle cx="13.5" cy="7.5" r="1.3"/></svg>',
};

/**
 * The rail's two actions, which are Exports.vue's two best ones lifted out of the sheet.
 * The logic stays in kitchen/, so both callers do the same thing.
 */
const canAr = arSupported();
const busyRail = ref(null);
async function showInRoom() {
  busyRail.value = 'ar';
  try {
    kitchen.value?.select?.(null);
    const blob = await exportUsdz(kitchen.value);
    const r = await viewInAr(blob, `${(plan.value.name ?? 'kitchen').replace(/[^\w]+/g, '-').toLowerCase()}.usdz`);
    say({ opened: 'Point the phone at the floor and the kitchen stands on it.',
      shared: 'Choose Quick Look to see it in your room.',
      downloaded: 'Saved as a 3D model.' }[r] ?? null);
  } catch (e) {
    say(`Could not build the model: ${e?.message ?? e}`);
  } finally { busyRail.value = null; }
}
/** Put the camera back where the app framed it. Two fingers can take it a long way. */
function recentre() {
  kitchen.value?.recentre?.();
  say('Back to the whole kitchen.');
}

/** The share sheet. The link itself is made in there, in the same words for both kinds. */
const shareSheet = ref(false);
/** The USDZ of a room that arrived with a hosted share: an https URL, not a file on this device. */
const sharedRoomUrl = ref(null);
async function openRoomUrl() {
  const url = sharedRoomUrl.value;
  if (!url) return;
  // On a phone the share sheet is the way to Safari, where a USDZ opens in Quick Look;
  // window.open inside the app's own WebView would navigate the app away from itself.
  if (isNative()) { try { await shareLink(url, { title: 'The room as scanned' }); } catch { /* cancelled */ } return; }
  window.open(url, '_blank', 'noopener');
}

const shownHint = ref('');
const shownRefusal = ref('');
watch(hint, (v) => { if (v) shownHint.value = v; });
watch(refusal, (v) => { if (v) shownRefusal.value = v; });

// A tab keeps the sheet where you left it; a different tab starts at its top.
watch(mode, () => { nextTick(() => { if (sheetEl.value) sheetEl.value.scrollTop = 0; }); });
const kitchen = shallowRef(null);
const selected = ref(null);
const selectedRun = ref(null);
const nudge = ref(0);            // bumped to re-read the plan after a scene change

const colour = computed(() =>
  maker.colours.find((c) => c.id === plan.value.colourId) ?? maker.colours[0]);
const bench = computed(() =>
  maker.benchtops.find((b) => b.id === plan.value.benchtopId) ?? maker.benchtops[0]);

/*
 * `selected` is a PLACED-CABINET -- one box standing in a run, at the width it will actually
 * be built. `selectedSpec` is the CABINET-SPEC behind it: the catalogue entry, which carries
 * the name, the real height and depth, and the range the maker will cut it at.
 *
 * Worth spelling out because the sheet shows both at once and they disagree on purpose: the
 * heading is the spec's name and the width beside it is the placed cabinet's, which is how a
 * "600 base" can honestly read 630mm.
 */
const selectedSpec = computed(() =>
  maker.cabinets.find((c) => c.id === selected.value?.specId));

/** Only cabinet-specs of the same KIND: an overhead cannot become a pantry. */
/*
 * What this cabinet could be instead.
 *
 * A run's kind is `base` or `wall`, but a TALL unit stands in a base run -- so filtering on
 * the run's kind alone offered base cabinets when you tapped the fridge tower and never
 * offered a pantry at all. Anything that stands on the floor can swap with anything else
 * that stands on the floor; an overhead can only be another overhead. The kind you are
 * already looking at comes first, because that is the row you are comparing against.
 */
const swappable = computed(() => {
  const kind = selectedRun.value?.kind;
  if (kind !== 'base') return maker.cabinets.filter((c) => c.kind === kind);
  const mine = selectedSpec.value?.kind === 'tall' ? 'tall' : 'base';
  const other = mine === 'tall' ? 'base' : 'tall';
  return [...maker.cabinets.filter((c) => c.kind === mine),
    ...maker.cabinets.filter((c) => c.kind === other)];
});

const cabs = computed(() => (kind.value === 'appliance'
  // In the order somebody shops: the two holes in the benchtop, then the three machines,
  // then the hood -- not the order the range happens to list them in.
  ? APPLIANCE_ROLES.flatMap((r) => maker.cabinets.filter((c) => c.role === r))
  : maker.cabinets.filter((c) => c.kind === kind.value)));
const kindWord = computed(() => KINDS.find((k) => k.id === kind.value)?.word ?? 'base');

/** The option the plan currently holds for a finish row. */
function current(f) {
  const list = maker[f.list];
  return list.find((o) => o.id === plan.value[f.id]) ?? list[0];
}
/** Which roles are actually standing in this kitchen, so the fittings rows can hide. */
const placedRoles = computed(() => {
  const out = new Set();
  for (const r of plan.value.runs ?? []) {
    for (const it of r.items ?? []) {
      const role = maker.cabinets.find((c) => c.id === it.specId)?.role;
      if (role) out.add(role);
    }
  }
  return out;
});
const finishRows = computed(() => FINISHES.filter(
  (f) => maker[f.list]?.length && (!f.needs || placedRoles.value.has(f.needs))));

function chipFor(f) { return optionChip(f, current(f)); }
function optionChip(f, o) {
  if (f.id === 'doorStyleId') return { background: colour.value.colour };
  if (f.id === 'colourId' || f.id === 'kickColourId') return finishChip(o);
  if (f.id === 'benchtopId') return stoneChip(o);
  if (f.id === 'splashbackId') return { background: o.colour ?? bench.value.colour };
  if (f.id === 'rangehoodId' || f.id === 'dishwasherId' || f.id === 'ovenId') {
    return { background: 'var(--k-fill)' };
  }
  if (f.id === 'floorId') return floorChip(o);
  if (f.id === 'wallColourId') return { background: o.colour };
  return { background: 'var(--k-fill)' };
}

/*
 * The sheet gets out of the way.
 *
 * It covers the bottom half of the screen, which is right while you are choosing something
 * and wrong the moment you want to look at what you chose -- and on the plan it was covering
 * the very walls it exists to change. The grab handle is a handle now: drag it down and the
 * sheet goes, drag it up and it comes back, and the camera reframes either way because it is
 * told how much of the window is still covered.
 *
 * `y` is how far down the sheet is pushed, in px. 0 is open; `hiddenY` leaves the handle
 * showing above the bar so there is always something to pull.
 */
const sheetEl = ref(null);
const sheetH = ref(0);
const barH = ref(0);
const y = ref(0);
const sliding = ref(false);
const down = computed(() => y.value > 4);
const sheetStyle = computed(() => ({ transform: `translateY(${y.value}px)` }));
const hiddenY = () => Math.max(0, sheetH.value - 30);
/** How much of the window something else is standing on. The camera and the plan both use it. */
const docked = ref(false);
const covered = computed(() => (docked.value ? 0 : Math.max(0, sheetH.value - y.value) + barH.value));
let grab = null;

function grabSheet(e) {
  try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is a nicety */ }
  grab = { startY: e.clientY, from: y.value, at: e.timeStamp, moved: false };
  sliding.value = true;
}
function dragSheet(e) {
  if (!grab) return;
  const dy = e.clientY - grab.startY;
  if (!grab.moved && Math.abs(dy) < 3) return;
  grab.moved = true;
  // Rubber-band past the ends rather than stopping dead: a sheet that ignores your finger
  // reads as broken, and one that slides off the screen cannot be got back.
  const raw = grab.from + dy;
  const max = hiddenY();
  y.value = raw < 0 ? raw / 3 : (raw > max ? max + (raw - max) / 3 : raw);
}
function dropSheet(e) {
  if (!grab) return;
  const g = grab;
  grab = null;
  sliding.value = false;
  try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
  if (!g.moved) { toggleSheet(); return; }
  // A flick decides on its own; a slow drag decides by where it ended up.
  const dy = e.clientY - g.startY;
  const speed = dy / Math.max(1, e.timeStamp - g.at);
  if (Math.abs(speed) > 0.5) y.value = speed > 0 ? hiddenY() : 0;
  else y.value = y.value > sheetH.value * 0.3 ? hiddenY() : 0;
}
function toggleSheet() { y.value = down.value ? 0 : hiddenY(); }
function openSheet() { y.value = 0; }

/** Switch mode; a second tap on the mode already showing puts the sheet away. */
function go(m) {
  if (mode.value === m && !selected.value) { toggleSheet(); return; }
  close();
  mode.value = m;
  finishOpen.value = null;
  openSheet();
}

/**
 * Pictures they bring.
 *
 * A render, a photo, a plan: picked, shrunk on the device, kept on the plan. A plan is also
 * handed to the reader, when the server has one -- see `readPlanImage`.
 */
const busy = ref(false);
const kindName = (id) => IMAGE_KINDS.find((k) => k.id === id)?.name ?? id;
async function addImage(kind) {
  if (readOnly()) return;
  busy.value = true;
  try {
    const raw = await pickImage();
    if (!raw) return;
    const small = await shrink(raw);
    const r = attachImage(plan.value, { kind, dataUrl: small.dataUrl, bytes: small.bytes });
    if (!r.ok) { say(r.why); return; }
    nudge.value++;
    await readImage(r.image, kind);
  } catch (e) {
    say(String(e?.message ?? e));
  } finally {
    busy.value = false;
  }
}
function dropImage(im) {
  plan.value.images = (plan.value.images ?? []).filter((i) => i.id !== im.id);
  nudge.value++;
}
function showImage(im) { window.open(im.dataUrl, '_blank'); }
/**
 * What the server can do. Read once; the buttons that need a key exist only if it has one.
 * A server without a key answers honestly and the app asks nothing of it.
 */
const features = ref({ chat: false, roomFromImage: false, readImage: false });
fetch('/api/health').then((r) => (r.ok ? r.json() : null)).then((h) => { if (h) features.value = h; }).catch(() => {});

/**
 * A photo of the plan, read for the walls.
 *
 * The reader returns lengths and a shape; nothing is applied until the person has looked
 * at the numbers and pressed the button, because a homeowner's photo of a builder's drawing
 * is not a quote and the reader can be wrong.
 */
/**
 * The room, scanned.
 *
 * On an iPhone Pro the LiDAR gives every wall as a segment; roomplan.js joins them into the
 * loop the planner wants, and the walls take over from whatever was there. Presets apply by
 * the walls' names where the scan is a box; anything else keeps its runs on the back wall
 * and the person taps the others on the plan.
 */
const scan = ref({ ok: false, reason: 'missing', platform: 'web' });
const scanWhy = computed(() => scanReason(scan.value));
scanStatus().then((s) => { scan.value = s; }).catch(() => {});
/**
 * The scan is offered with instructions first.
 *
 * RoomPlan coaches you once it is running, and none of that coaching says the thing that
 * decides whether the scan works: walk the WHOLE room. Somebody who does three walls and
 * presses Done is told the room did not close, which is true, and by then the phone is
 * down. Shown once; the tick remembers.
 */
const drawings = ref(false);
const scanIntro = ref(false);
function scanRoom() {
  if (readOnly()) return;
  if (prefs.seenScanIntro) { startScan(); return; }
  scanIntro.value = true;
}
function startScan(remember = false) {
  scanIntro.value = false;
  if (remember) prefs.seenScanIntro = true;
  runTheScan();
}

/**
 * ── A SCAN THAT CAME BACK IS NEVER LOST AND NEVER DEAD-ENDS ───────────────────────────
 *
 * The owner scanned his kitchen on a TestFlight build. RoomPlan drew its boxes round the
 * cupboards, the dishwasher and the sink, he pressed Done -- and the app showed him the
 * Start card again with the demo kitchen behind it, no error, no way on. "It scanned but
 * then wouldn't let me progress."
 *
 * Every failure path here used to throw the whole thing away and say one pill that fades in
 * three seconds: a minute of walking round a kitchen, Apple's own result, the appliances it
 * recognised and the USDZ it wrote, gone before the phone was back up. So:
 *
 *   1. the raw payload is held the instant it arrives, BEFORE anything that can fail;
 *   2. every failure is a CARD in the Room sheet, not a pill -- it says which of the three
 *      it was, offers a scan again with the one piece of advice that decides whether the
 *      next one works, offers the typed walls, and always offers the report and the 3D file;
 *   3. a cancelled scan is not a failure and shows nothing at all.
 *
 * `take` is the scanner, injectable so the dev hook can drive all three paths from a laptop
 * with no LiDAR in it. Nothing else ever passes it.
 */
async function runTheScan(take = runScan) {
  busy.value = true;
  scanProblem.value = null;
  scanLoop.value = null;
  try {
    let scanned = null;
    try {
      scanned = await take();
    } catch (e) {
      /*
       * THE SCANNER ITSELF WENT WRONG. Distinct in code from the line below, and it has to
       * be: `scan()` in the plugin returns null for a cancel and throws for everything else,
       * so the two arrive by different doors and must leave by different ones.
       */
      scanFailed('error', String(e?.message ?? e), null, { scanned: false });
      return;
    }
    /*
     * CANCELLED, which is not an error and never was. Somebody pressed Cancel, or backed out
     * of the camera; they are on the Start card, which is where they wanted to be. No card,
     * no pill, nothing said -- and `busy` clears in the `finally` below whichever way this
     * went.
     */
    if (!scanned) return;

    /*
     * HELD BEFORE ANYTHING CAN FAIL. Everything below this line can throw or refuse, and
     * from here on none of that can cost the person the scan: the payload is in a ref, the
     * USDZ path with it, and `scanReport()` can write the whole thing out whether or not a
     * single wall was ever built from it.
     */
    rawScan.value = scanned;
    scanModel.value = scanned.modelPath ?? null;

    /*
     * WHICH OF THE ROOMS IT WALKED THROUGH IS THE KITCHEN.
     *
     * Somebody scanning their kitchen walks through the archway into the living room,
     * because that is where you stand to see the far end of the island -- so the scan comes
     * back with two rooms' walls, and both of them close. `loopFromSegments` picks the
     * tightest room that holds the most of what follows, and what follows is the only thing
     * in the payload that can tell a kitchen from a lounge of the same size: the cupboards,
     * the sink, the cooktop, the oven, the dishwasher, the fridge, and the benchtops the
     * LiDAR measured. A benchtop is in a kitchen by definition.
     *
     * Deliberately NOT every object. RoomPlan returns chairs, tables and sofas too, and the
     * living room has more of those than the kitchen does -- handing those in as evidence
     * would vote the lounge in as the room to fit out.
     */
    const inside = [
      ...(scanned.objects ?? []).filter((o) => roleOf(o?.category) !== undefined),
      ...(scanned.benches ?? []),
    ].filter((o) => Number.isFinite(o?.cx) && Number.isFinite(o?.cz));
    const r = loopFromSegments(scanned.walls ?? [], { inside });
    scanLoop.value = r;
    // A room that would not close. The loop's own sentence says which way it failed and is
    // shown on the card as the detail under our own words -- it is written for a person
    // standing in the room, which is exactly who is reading it.
    if (r.problem || !r.walls) { scanFailed('unclosed', r.problem, r); return; }
    await roomFromScan(scanned, r);
  } catch (e) {
    /*
     * Anything after the loop -- reproducing the cabinets, writing the Original, the scene.
     * It is still the app that broke rather than the scan, so it is the same card, and the
     * raw payload above it is untouched.
     */
    scanFailed('error', String(e?.message ?? e));
  } finally {
    busy.value = false;
  }
}

/** The scan worked. Everything from here down is the room it measured, taken up. */
async function roomFromScan(scanned, r) {
  rememberRoom();
  const has = new Set(r.walls.map((w) => w.id));

  // What was IN the room, not only the shape of it. A scan of a kitchen that came back as
  // an empty rectangle was the app throwing away everything Apple found except the walls.
  const found = fromScan(scanned, maker, r.walls, r.origin);
  // The room itself as a file was taken the moment the payload arrived, in `runTheScan`,
  // and is kept for the whole session so it can be looked at or handed to a maker from the
  // found-kitchen card, the problem card or the Review tab's exports -- whichever the
  // person reaches for, and whether or not any of the rest of this worked.
  scanLocks.value = Object.fromEntries((found.services ?? []).map((sv) => [sv.id, true]));
  const wanted = has.has('left') ? room.presetRuns('l') : room.presetRuns('single');
  plan.value.shape = has.has('left') ? 'l' : 'single';
  plan.value.runs = found.runs.length
    ? found.runs
    : wanted.filter(([wallId]) => has.has(wallId))
      .map(([wallId, kind]) => ({ id: `r-${wallId}-${kind}-${Date.now()}`, wallId, kind, items: [] }));
  plan.value.openings = found.openings;
  // The island the scan found, if it found one standing away from the walls.
  plan.value.islands = found.islands ?? [];
  kitchen.value.setWalls(r.walls);
  close();
  nudge.value++;

  const dropped = r.dropped ? ` ${r.dropped} small piece${r.dropped > 1 ? 's' : ''} ignored.` : '';
  /*
   * THE CARD SHOWS WHATEVER THE SCAN SAID, including that it found nothing.
   *
   * It used to appear only when a kitchen came back, so every reason a scan produced
   * nothing -- too few objects, all of them low confidence, categories this app does not
   * fit out -- was written into notes that were then thrown away. From the other end of a
   * phone that is indistinguishable from the scanner not working at all.
   */
  scanned_.value = found;
  /*
   * AND THE STEPPER STARTS HERE, whatever else was going on.
   *
   * A scan is the one event in this app that makes a person a beginner again: whatever
   * they had open, what is in front of them now is a room they have just measured and
   * three decisions they have never made. `typedWalls` goes back to false so the presets
   * that would replace the scan are not sitting under the card that offers to keep it.
   */
  flow.value = 'scanned';
  typedWalls.value = false;
  chosenOption.value = null;
  // A new room: last scan's answer to "which walls are the kitchen" is about a different
  // kitchen, and carrying it over would tick walls in a house they have never been in.
  kitchenPick.value = null;
  /*
   * SAVED BEFORE ANYBODY CAN TOUCH IT, whether or not a single cabinet was found: an
   * empty room's walls, doorways and USDZ are still the record of the room.
   *
   * Not offered, not confirmed, not "keep this?" -- written, here, in the same breath as
   * the scan. Every second between the scan finishing and the record being made is a
   * second in which a drag, a swap or a stray "Design it for me" destroys the only
   * account anybody has of what this kitchen looks like today. The person can delete it
   * later from My kitchens, deliberately and twice; that is the right way round.
   *
   * `plan.value` is the plan exactly as `fromScan` left it, and `found` carries what the
   * scan saw and what it found plumbed or gassed in. The USDZ goes with it as a path:
   * device-local, this device only, and never in a share link.
   */
  original.value = await saveOriginal(plan.value, found,
    { name: plan.value.name || 'As scanned', modelPath: scanModel.value });
  keepIslandInDesign.value = true;
  if (found.runs.length) {
    view.value = '3d';
    mode.value = 'room';
    openSheet();
    say(original.value
      ? 'Saved as scanned. It stays exactly as it is; every design starts from it.'
      : 'This browser would not keep the scan. The room is on screen; save the design to keep it.');
  } else {
    view.value = 'plan';
    // The card still comes up, and the sheet with it: a scan that found no cabinets found
    // a ROOM, and the one button on that card designs a kitchen for it.
    mode.value = 'room';
    openSheet();
    say(`${r.walls.length} walls.${dropped} ${found.saw?.objects
      ? 'Nothing in the room could be reproduced — see the note.'
      : 'Tap a wall to put cabinets on it, or design it for you.'}${
      original.value ? ' The room is saved as scanned.' : ''}`);
  }
}

/** What the last scan reproduced, until the person keeps it or asks for a new design. */
const scanned_ = ref(null);
/**
 * THE PLUGIN'S PAYLOAD, EXACTLY AS IT ARRIVED, held from the instant it arrived.
 *
 * Not what the app made of it -- what Apple handed over: every wall segment, every door,
 * window and opening, the benchtops and cupboard fronts it measured, every object it
 * recognised, and the paths of the files it wrote. This is the thing that used to be thrown
 * away the moment anything downstream refused, and it is the only account of a scan that
 * exists once the phone is back in a pocket. `scanReport()` writes it out; nothing edits it.
 */
const rawScan = ref(null);
/** What `loopFromSegments` said about those walls, kept whether it made a room or refused. */
const scanLoop = ref(null);
/**
 * Why the last scan produced no room: `{ kind: 'error' | 'unclosed', detail, scanned }`.
 *
 * There is no 'cancelled' -- a cancel leaves this null, because it is not a problem and
 * gets no card. `detail` is the machine's own words (an exception, or the loop's sentence)
 * and is never the whole of what a person is shown; see ScanFlow's problem stage.
 *
 * `scanned` is whether THIS failure has a payload behind it. A scanner that throws before it
 * hands anything over leaves `rawScan` and `scanModel` holding some earlier scan's room, and
 * offering an earlier kitchen's file and geometry under the words "the scan did not come
 * through" is worse than offering nothing. The earlier scan is not destroyed for it; it is
 * just not reported as this one.
 */
const scanProblem = ref(null);
/**
 * The scan came back and could not become a room. A CARD, not a pill.
 *
 * Nothing is cleared here on purpose: `rawScan`, `scanModel` and `scanLoop` are what the
 * card offers to copy and to open, and they outlive the failure. `scanned_` goes to null
 * because it means "a room was reproduced", and none was.
 */
function scanFailed(kind, detail, loop = null, { scanned = true } = {}) {
  if (loop) scanLoop.value = loop;
  scanProblem.value = { kind, detail: detail ? String(detail) : null, scanned };
  scanned_.value = null;
  flow.value = 'problem';
  typedWalls.value = false;
  mode.value = 'room';
  openSheet();
}
/** A `file://` path to the room as a USDZ, this session only -- never persisted or shared
    off the device except by the person's own choice. */
const scanModel = ref(null);
/** Which of the scan's plumbed-or-gassed items to lock in place, keyed by item id.
    Defaults to "keep them all" -- moving the sink is the exception, not the rule, and an
    unchecked box a person has to notice and tick is how a plumbing point quietly moves. */
const scanLocks = ref({});
const SERVICE_WORD = { sink: 'the sink', cooktop: 'the gas cooktop', oven: 'the oven', dishwasher: 'the dishwasher' };

/*
 * ── THE ORIGINAL ──────────────────────────────────────────────────────────────────────
 *
 * The room as scanned, saved the instant the scan came back and never written again. The
 * plan on screen is a DESIGN of that room and carries its id; the record itself lives in
 * storage and is loaded here so the page can offer to show it.
 *
 * Loaded by watching `plan.originalId` rather than being set where a design is made,
 * because a design also arrives by being opened from My kitchens, by a share link, and by
 * the autosave on boot -- three more places that would each have had to remember.
 */
const original = ref(null);
watch(() => plan.value.originalId, async (id) => {
  const found = id ? await loadOriginal(id) : null;
  // An Original that came with a hosted share is not in this device's storage and is not
  // to be thrown away for that: if what is held already answers to the id, it stays.
  if (!found && id && original.value?.id === id) return;
  original.value = found;
  // The room it was pointing at is gone (deleted from the Kitchens sheet, or a link opened
  // on a device that never had it): the design is still perfectly good, there is just
  // nothing to compare it to.
  if (!found && viewing.value === 'now') showDesign();
}, { immediate: true });

/** Whether the island's footprint comes across into a new design. Ticked: it usually should. */
const keepIslandInDesign = ref(true);

/** The USDZ of the room: this session's scan, or the one the Original remembers. */
const scanFile = computed(() => original.value?.modelPath ?? scanModel.value ?? null);

/**
 * The one rule the Original has: nothing may write to it.
 *
 * Called first by every handler that changes the plan. It is a page-level guard on purpose
 * -- `touch.js` and `scene.js` are plain three.js with no idea that a room can be a record,
 * and putting the notion in there would spread it through the one part of this app that is
 * worth keeping simple. What touch.js DOES understand is a lock, so the copy handed to the
 * scene has every one of its cabinets locked, which is what makes a drag refuse; the tap
 * that a locked cabinet still allows lands in `onSelect`, and is refused here.
 */
function readOnly() {
  if (viewing.value !== 'now') return false;
  hint.value = 'This is the room as scanned. Switch back to New to change anything.';
  return true;
}

/** Show the room as scanned: a copy of it, with everything in it locked. */
function showOriginal() {
  const o = original.value;
  if (!o) return;
  const asPlan = deserialize(o);
  if (!asPlan) { say('That scan would not open.'); return; }
  // Every cabinet locked, so `touch.js` refuses to drag any of them without knowing why.
  asPlan.locks = asPlan.runs.flatMap((r) => r.items.filter((i) => i.id).map((i) => ({ itemId: i.id })));
  viewing.value = 'now';
  view.value = '3d';
  // The assistant can change things, so it does not get to stand over a record.
  if (mode.value === 'ask') mode.value = 'room';
  close();
  // No hint pill: the note beside the control says this permanently, and a pill that
  // fades is the wrong shape for a fact that stays true the whole time it is up.
  kitchen.value.setPlan(asPlan);
}

/** Back to the design, which is the only thing `plan` ever was. */
function showDesign() {
  viewing.value = 'new';
  hint.value = null;
  kitchen.value?.setPlan(plan.value);
  // A look at some other day's room, from the Kitchens sheet, ends when it is over: the
  // Now/New control belongs to the design's OWN Original, and that one was not it.
  if (original.value && original.value.id !== plan.value.originalId) {
    const own = plan.value.originalId;
    original.value = null;
    // ...and the design's OWN room comes back, if it has one: a look at last month's
    // scan must not take this design's Now away with it.
    if (own) loadOriginal(own).then((o) => { if (o && plan.value.originalId === own) original.value = o; });
  }
}

/** 3D or Plan. The plan drawing is of the design, so asking for it comes back from Now. */
function goView(v) {
  if (v === 'plan' && viewing.value === 'now') showDesign();
  view.value = v;
}
const serviceLine = computed(() => {
  const words = (scanned_.value?.services ?? []).map((sv) => SERVICE_WORD[sv.role] ?? sv.role);
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
});

/** Open the room as scanned: on iOS the share sheet itself previews a USDZ, spinnable and
    to scale, before anyone picks where it goes -- so this is "look at it" and "hand it to
    the maker" in the one tap. */
async function openScanFile(path) {
  if (!path) return;
  try {
    const r = await shareNativeFile(path,
      { title: 'The kitchen as it is today', text: 'Scanned with LiDAR' });
    if (r === 'shared') say('Sent.');
  } catch (e) {
    say(`Could not open the scan: ${e?.message ?? e}`);
  }
}
function shareScanModel() { return openScanFile(scanFile.value); }
/*
 * THE FILE THIS SCAN WROTE, as against the one the design remembers.
 *
 * On the problem card those are not the same thing and the difference matters: `scanFile`
 * prefers the Original's model, which on a failed scan is still LAST week's kitchen, and
 * handing somebody the wrong room is worse than handing them nothing. RoomPlan's own model
 * is worth having even when we could not read the room, so the card opens exactly the file
 * the scan that just failed produced.
 */
function shareRawScan() { return openScanFile(scanModel.value); }
/** One line of what Apple actually handed back, in the order it matters. */
const sawLine = computed(() => {
  const s = scanned_.value?.saw;
  if (!s) return '';
  const bits = [`${s.walls} wall${s.walls === 1 ? '' : 's'}`];
  if (s.doors) bits.push(`${s.doors} door${s.doors > 1 ? 's' : ''}`);
  if (s.windows) bits.push(`${s.windows} window${s.windows > 1 ? 's' : ''}`);
  if (s.openings) bits.push(`${s.openings} opening${s.openings > 1 ? 's' : ''}`);
  bits.push(`${s.objects} thing${s.objects === 1 ? '' : 's'} in the room`);
  // What it MEASURED, as against what it recognised. A benchtop is the evidence RoomPlan's
  // classifier cannot give: it has one word for the whole of fitted joinery and finds a
  // wall of cupboards as one uncertain box, where the bench over them is unmistakable.
  if (s.benches) bits.push(`${s.benches} stretch${s.benches > 1 ? 'es' : ''} of benchtop`);
  if (s.fronts) bits.push(`${s.fronts} run${s.fronts > 1 ? 's' : ''} of cupboard fronts`);
  return `The scan returned ${bits.join(', ')}.`;
});

/*
 * WALLS THAT WERE NOT PART OF THIS ROOM, and why that is not a failure.
 *
 * Somebody scanning a kitchen walks through the doorway more often than not, and the scanner
 * keeps measuring: the hall, the corner of the dining room, the back of the laundry. The
 * loop picks the room it can close and sets the rest aside, which is right -- the kitchen is
 * what it came for -- but a room that comes out smaller than the phone was pointed at needs
 * saying out loud, or it reads as walls gone missing. One line, in the details, beside the
 * counts it belongs with.
 *
 * `leftOver` may be a count or the segments themselves and may not be there at all: this
 * reads whatever `loopFromSegments` returns and says nothing when there is nothing to say.
 */
/** How many segments the loop set aside, however it says so. A count, or a list of them. */
const countOf = (v) => (Array.isArray(v) ? v.length : Math.round(Number(v) || 0));
const leftOverLine = computed(() => {
  const n = countOf(scanLoop.value?.leftOver);
  if (!n) return '';
  const mm = Math.round(Number(scanLoop.value?.leftOverLength) || 0);
  const much = mm ? ` (${mm}mm of wall)` : '';
  return `${n === 1 ? 'One wall was' : `${n} walls were`} left out${much}: they are not part of`
    + ' this room, and are almost always the next room seen through a doorway.';
});

/**
 * A list, in English: "a, b and c".
 *
 * With `oxford`, a comma before the final "and" -- which is not a house-style preference
 * here, it is the only thing that stops a list whose LAST ITEM is itself a list from running
 * two "and"s together: "the oven, cooktop, sink and dishwasher and an island".
 */
function andList(words, oxford = false) {
  if (words.length <= 1) return words[0] ?? '';
  const join = oxford && words.length > 2 ? ', and ' : ' and ';
  return `${words.slice(0, -1).join(', ')}${join}${words[words.length - 1]}`;
}
/** The service roles in the shortest honest word, for the sentence rather than the ticks. */
const SERVICE_SHORT = { sink: 'sink', cooktop: 'cooktop', oven: 'oven', dishwasher: 'dishwasher' };

/**
 * ONE SENTENCE OF WHAT CAME BACK.
 *
 * `sawLine` is the counts as the scanner reported them -- "4 walls, 3 windows, 20 things in
 * the room, 2 stretches of benchtop" -- which is the right thing to put in a support message
 * and the wrong thing to put in front of somebody standing in their kitchen. This is the
 * same scan in the words they would use: what is in the room, not what the classifier
 * returned. The counts are still there, one tap away, behind "See the details".
 */
const foundLine = computed(() => {
  nudge.value;
  const f = scanned_.value;
  if (!f) return '';
  const s = f.saw ?? {};
  const bits = [];
  const many = (k, one, more) => (k === 1 ? one : `${k} ${more}`);
  if (s.walls) bits.push(many(s.walls, 'one wall', 'walls'));
  if (s.doors) bits.push(many(s.doors, 'a doorway', 'doorways'));
  if (s.windows) bits.push(many(s.windows, 'a window', 'windows'));
  if (s.openings) bits.push(many(s.openings, 'an opening', 'openings'));
  // The appliances are named on their own below, so they are not counted twice as cupboards.
  const services = new Set((f.services ?? []).map((sv) => sv.id));
  const cupboards = (f.runs ?? []).reduce((n, r) => n
    + (r.items ?? []).filter((i) => i.specId && !i.filler && !services.has(i.id)).length, 0);
  if (cupboards) bits.push(many(cupboards, 'one cupboard', 'cupboards'));
  const named = (f.services ?? []).map((sv) => SERVICE_SHORT[sv.role] ?? sv.role);
  if (named.length) bits.push(`the ${andList(named)}`);
  if (plan.value.islands?.length) bits.push('an island');
  if (!bits.length) return 'The scan came back with nothing it could measure.';
  return `The scan found ${andList(bits, bits.some((b) => b.includes(' and ')))}.`;
});

/** What went wrong, for the first line of the report. Never the whole of what a person reads. */
const PROBLEM_WORD = {
  error: 'the scanner errored',
  unclosed: 'the room could not be closed',
};

/**
 * EVERY NUMBER THE PHONE PRODUCED, as text, and it works when nothing else did.
 *
 * A scan happens on a phone in somebody's kitchen and goes wrong there. Nothing about it
 * reaches this end, so "it found no cabinets" -- or worse, "it scanned but then wouldn't let
 * me progress" -- is where the conversation stops. This is the whole bridge, and the case it
 * has to survive is the one where NO ROOM WAS EVER BUILT: no planner frame, no reproduced
 * cabinets, nothing but what Apple handed over.
 *
 * Two shapes, and the header line of the geometry block says which:
 *
 *   planner frame  the room closed. `reproduce.js` wrote `saw.geometry` in the app's own
 *                  squared-up millimetres, which is what every reader downstream works in.
 *   phone frame    the room did not close, so there is no planner frame to be in. The wall
 *                  segments go out as the plugin gave them -- centre, direction, length --
 *                  and `loopFromSegments` can be run against exactly those numbers, which
 *                  is the whole of the bug being reported.
 *
 * The line formats are `reproduce.js`'s, unchanged, because `scripts/scan-fixture.mjs` eats
 * them: one leading word saying what the line is, then millimetres, nothing that a chat
 * window will autocorrect. The wall line in the phone-frame block carries its endpoints AND
 * its raw `c=`/`d=`/`len=` fields -- the endpoints so the fixture script reads it as a wall,
 * the raw fields because those are the numbers the loop actually refused.
 */
function scanReport() {
  const p = scanProblem.value;
  const s = scanned_.value?.saw ?? null;
  // A failure the scanner threw before handing anything over reports itself and nothing
  // else: whatever is still held belongs to an earlier scan and is not this one's evidence.
  const raw = p && !p.scanned ? null : rawScan.value;
  if (!s && !raw && !p) return '';
  const L = scanLoop.value;
  const lines = [`Harfred Planner scan — ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`];

  // What went wrong, at the top, in the same words the card said it in -- a report that
  // buries the failure under twenty lines of counts is a report nobody reads to the end of.
  if (p) lines.push(`did not finish: ${PROBLEM_WORD[p.kind] ?? p.kind}${p.detail ? ` — ${p.detail}` : ''}`);
  // WHAT THE DEVICE SAID IT COULD DO, which is the whole report when the scanner threw
  // before it produced anything: "ios, scanner available" and an exception is a bug report;
  // an exception on its own is a sentence.
  lines.push(`device: ${scan.value.platform ?? '?'}, scanner `
    + `${scan.value.ok ? 'available' : `unavailable (${scan.value.reason ?? '?'})`}`);

  if (s) {
    lines.push(
      `walls ${s.walls}, doors ${s.doors}, windows ${s.windows}, openings ${s.openings}`
        + ` (${s.usedOpenings} used), objects ${s.objects}`,
      `measured: benches ${s.benches ?? 0}, cupboard fronts ${s.fronts ?? 0}`,
      ...Object.entries(s.kinds ?? {}).map(([k, n]) => `  ${n} x ${k}`),
      /*
       * And every object one line each: category, confidence, width.
       *
       * The counts alone could not be read from this end. The owner's scan reported "20
       * objects, 14 cabinets" and nothing in that says the sink, the stove, the oven and the
       * dishwasher were all found, all correctly named, and all dropped for confidence --
       * which is exactly what had happened, and it took a second scan to learn it.
       */
      ...(s.things ?? []).map((t) => `  ${t.category} · ${t.confidence} · ${t.length}mm`),
      s.unknown?.length ? `not recognised: ${s.unknown.join(', ')}` : 'every category recognised',
      ...(scanned_.value.notes ?? []).map((n) => `note: ${n}`),
    );
  } else if (raw) {
    // Nothing was reproduced, so there are no reproduced counts. These are the plugin's own
    // list lengths, which is every count that exists at this point.
    lines.push(
      `the scanner returned: walls ${raw.walls?.length ?? 0}, doors ${raw.doors?.length ?? 0}`
        + `, windows ${raw.windows?.length ?? 0}, openings ${raw.openings?.length ?? 0}`
        + `, objects ${raw.objects?.length ?? 0}`,
      `measured: benches ${raw.benches?.length ?? 0}, cupboard fronts ${raw.fronts?.length ?? 0}`,
      ...Object.entries((raw.objects ?? []).reduce((acc, o) => {
        const k = String(o?.category ?? '?');
        acc[k] = (acc[k] ?? 0) + 1;
        return acc;
      }, {})).map(([k, n]) => `  ${n} x ${k}`),
    );
  } else {
    lines.push('the scanner returned nothing at all — it stopped before it handed the room over');
  }

  // What the loop made of the walls, including the two counts that explain a room that came
  // out smaller than the kitchen it was standing in.
  if (L) {
    const dropped = countOf(L.dropped);
    const left = countOf(L.leftOver);
    lines.push(`loop: ${L.walls?.length ?? 0} walls in the room, ${dropped} dropped as too short`
      + `, ${left} left out${left ? ` (${Math.round(Number(L.leftOverLength) || 0)}mm)` : ''}`
      + `${L.problem ? `, refused: ${L.problem}` : ''}`);
  }
  lines.push(`3D file: ${raw && scanModel.value ? 'written' : 'none'}`);

  if (s?.geometry?.length) lines.push('geometry (mm, planner frame)', ...s.geometry);
  else if (raw) {
    lines.push('geometry (mm, the phone\'s own frame — no room was built, so there is no'
      + ' planner frame)', ...rawGeometry(raw));
  }
  return lines.join('\n');
}

/** The plugin's payload, one line per thing, in `reproduce.js`'s formats. See `scanReport`. */
function rawGeometry(raw) {
  const n = (v) => Math.round(Number(v) || 0);
  const d = (v) => Math.round((Number(v) || 0) * 1000) / 1000;
  const out = [];
  (raw.walls ?? []).forEach((w, i) => {
    const hx = (Number(w.dx) || 0) * (Number(w.length) || 0) / 2;
    const hz = (Number(w.dz) || 0) * (Number(w.length) || 0) / 2;
    out.push(`wall s${i} ${n(w.cx - hx)},${n(w.cz - hz)} ${n(w.cx + hx)},${n(w.cz + hz)}`
      + ` c=${n(w.cx)},${n(w.cz)} d=${d(w.dx)},${d(w.dz)} len=${n(w.length)}`);
  });
  // A hole with no wall to belong to. `at=` along a wall is exactly what could not be worked
  // out here, so these go out as the segments they are and say so by their fields.
  for (const [kind, list] of [['door', raw.doors], ['window', raw.windows], ['opening', raw.openings]]) {
    for (const o of list ?? []) {
      out.push(`${kind} c=${n(o.cx)},${n(o.cz)} d=${d(o.dx)},${d(o.dz)} len=${n(o.length)}`
        + ` h=${n(o.height)} cy=${n(o.cy)}`);
    }
  }
  for (const b of raw.benches ?? []) {
    out.push(`bench ${n(b.cx)},${n(b.cz)} ${d(b.dx)},${d(b.dz)} ${n(b.length)} ${n(b.depth)} ${n(b.y)}`);
  }
  for (const f of raw.fronts ?? []) {
    out.push(`front ${n(f.cx)},${n(f.cz)} ${d(f.dx)},${d(f.dz)} ${n(f.length)} ${n(f.depth)} ${n(f.y0)} ${n(f.y1)}`);
  }
  for (const o of raw.objects ?? []) {
    if (!o) continue;
    out.push(`${String(o.category ?? '?').replace(/\s+/g, '')} ${String(o.confidence ?? '?')} `
      + `${n(o.cx)},${n(o.cz)} ${d(o.dx)},${d(o.dz)} ${n(o.length)} ${n(o.height)} ${n(o.depth)}`
      + ` cy=${n(o.cy)}`);
  }
  return out;
}

async function copyScan() {
  const text = scanReport();
  if (!text) { say('There is nothing from a scan to copy yet.'); return; }
  const r = await copyText(text);
  say(r === 'copied' ? 'Copied. Paste it wherever you are reporting this.' : 'Could not copy.');
}

/*
 * FROM THE SCAN TO A DESIGN, two ways, neither of which edits the scan.
 *
 * The Original was written the moment the scan came back. Both of these ask `designFrom`
 * for a fresh plan built on that room -- the walls, the doorways, the windows, the ceiling,
 * and whatever the ticks say about the island and the services -- and hand it to `takePlan`,
 * which is the same one step that opening a saved kitchen is.
 */
const lockedServices = () => (scanned_.value?.services ?? [])
  .filter((sv) => scanLocks.value[sv.id])
  .map((sv) => ({ id: sv.id, role: sv.role }));

function fromTheScan({ keepLayout }) {
  const o = original.value;
  if (!o) { say('The scan was not kept, so there is nothing to design from.'); return null; }
  const chosen = lockedServices();
  /*
   * The walls they picked reach the generator HERE, and nowhere else.
   *
   * `designFrom` builds a fresh plan from the Original -- which is never edited, so a person
   * who changes their mind and comes back through the step gets an answer as good as the
   * first one -- and `onlyWalls` is what stops it laying a preset across a room that has no
   * preset shape. Null on every path that never asked, which is every design opened from My
   * kitchens, every share link, and a room whose walls were typed in.
   */
  const p = designFrom(o, maker, { keepLayout, lockServices: chosen, keepIsland: keepIslandInDesign.value,
    onlyWalls: kitchenPick.value ? [...kitchenPick.value] : null });
  scanned_.value = null;
  // The plan first, then the view: `showDesign` reads the plan to decide whether the
  // Original on screen is still this design's own.
  takePlan(p);
  showDesign();
  return chosen;
}

/*
 * ── WHICH WALLS ARE THE KITCHEN ───────────────────────────────────────────────────────
 *
 * THE OWNER'S OPEN-PLAN SCAN, AND HIS OWN ANSWER TO IT.
 *
 * Build 13's scan worked and came back with the whole floor: twenty walls, kitchen plus
 * dining plus living room, one closed loop, all of it true. So the design came out spread
 * across the lot -- cupboards proposed along living-room walls, and a vast empty middle.
 * His words: "Add a step where you can drag / draw / define what walls you'd like to be
 * available for kitchen after the initial scan."
 *
 * Nothing in the geometry can answer it, because nothing in the room does: an open-plan
 * kitchen ends where the family says it ends. So it is asked, once, on the plan, between
 * the scanned card and the three kitchens.
 *
 * WHAT IS NOT HAPPENING HERE. The walls are not trimmed, moved or thrown away. The scan
 * measured the whole floor and that is the truth about the house; the drawings, the
 * doorways and the 3D room keep showing all of it. The only thing being chosen is which
 * segments may carry cabinets.
 *
 * `kitchenPick` is a Set of segment ids -- walls and islands alike, because `room.segments`
 * makes no distinction and neither does a run. Null whenever the step is not up.
 */
const kitchenPick = ref(null);
/** What PlanView is handed: the picked ids while picking, null the rest of the time. */
const pickIds = computed(() =>
  (flow.value === 'walls' && kitchenPick.value ? [...kitchenPick.value] : null));

/**
 * The step opens, with the scan's own guess already ticked.
 *
 * `kitchenWalls` is the guess and it is pure, so it is testable against a real scan without
 * a browser -- which is the only way to know whether "close to right" is true of somebody
 * else's kitchen. Where there is no raw payload to read (a design being looked at again,
 * the dev fixture's shortcut) the walls that already carry runs stand in for it: they are
 * what the scan found, one step further on.
 */
function pickWalls() {
  const walls = plan.value.walls ?? [];
  if (!walls.length) { onwardFromWalls(); return; }
  // Back out of the step and in again and the ticks are where they were left. Only a new
  // scan starts the guess over -- see `roomFromScan`, which clears this.
  if (!kitchenPick.value) {
    const guess = rawScan.value && scanLoop.value?.walls
      ? kitchenWalls(rawScan.value, scanLoop.value.walls, scanLoop.value.origin).walls
      : (scanned_.value?.runs ?? []).map((r) => r.wallId);
    const ids = new Set(guess.filter((id) => walls.some((w) => w.id === id)));
    // The island comes in ticked, as it does on the card before this one, and for the same
    // reason: keeping it is nearly always right.
    if (keepIslandInDesign.value) for (const s of plan.value.islands ?? []) ids.add(s.id);
    kitchenPick.value = ids;
  }
  flow.value = 'walls';
  mode.value = 'room';
  // The plan, always: this is a question about walls and it is answered with a finger on
  // one. In 3D a wall is a place where two surfaces happen to overlap on screen.
  goView('plan');
  openSheet();
  nudge.value++;
}

/** One wall, or the island, in or out. The island's tick is the one the card already has. */
function togglePick(id) {
  const ids = new Set(kitchenPick.value ?? []);
  if (ids.has(id)) ids.delete(id); else ids.add(id);
  kitchenPick.value = ids;
  if ((plan.value.islands ?? []).some((s) => s.id === id)) keepIslandInDesign.value = ids.has(id);
  nudge.value++;
}
function pickAll() {
  kitchenPick.value = new Set(room.segments(plan.value).map((s) => s.id));
  keepIslandInDesign.value = true;
  nudge.value++;
}
function pickNone() {
  kitchenPick.value = new Set();
  keepIslandInDesign.value = false;
  nudge.value++;
}

/**
 * How much kitchen the ticks add up to, in the one sentence the card shows.
 *
 * Metres of bench, not walls, because that is what a person knows their own kitchen in --
 * and it is `room.extents` that says how long a run on a wall actually comes out, once the
 * corners and the doorways have been paid for. Base runs only: an overhead follows the run
 * under it and counting both would double every number.
 */
const pickLine = computed(() => {
  nudge.value;
  const ids = kitchenPick.value;
  if (!ids) return '';
  const isle = (plan.value.islands ?? []).filter((s) => ids.has(s.id));
  const walls = (plan.value.walls ?? []).filter((w) => ids.has(w.id));
  if (!walls.length && !isle.length) return 'Nothing chosen yet — tap the walls your kitchen stands against.';
  const runs = [...walls, ...isle].map((s, i) => ({ id: `pick-${i}`, wallId: s.id, kind: 'base', items: [] }));
  const ext = room.extents({ ...plan.value, runs }, maker.limits);
  const mm = runs.reduce((n, r) => n + Math.max(0, (ext[r.id]?.to ?? 0) - (ext[r.id]?.from ?? 0)), 0);
  const what = [walls.length ? `${walls.length} wall${walls.length > 1 ? 's' : ''}` : '',
    isle.length ? 'the island' : ''].filter(Boolean).join(' and ');
  return `${what} — ${(mm / 1000).toFixed(1)} m of cabinets`;
});

/**
 * The picks, back into the plan, from the scan rather than from what is on screen.
 *
 * The scan is the record and it is never edited, so every trip through this step starts
 * from it again: change your mind twice and the third answer is as good as the first.
 * `only` is subtractive -- the evidence still decides what kind of run a wall gets, this
 * only keeps the ones that were chosen -- and the walls left out keep their doorways, their
 * windows and their geometry, because they are still the room.
 *
 * Where there is no payload to re-read (the dev fixture's shortcut, a scan restored from
 * storage) the Original's own runs are filtered instead. Same rule, one step further on.
 */
function applyPicks() {
  const ids = kitchenPick.value;
  if (!ids) return;
  const only = [...ids];
  if (rawScan.value && scanLoop.value?.walls) {
    /*
     * The ticks about the plumbing, carried across BY WHAT THEY ARE rather than by item id.
     *
     * Re-reading the scan makes every run and every item afresh, and the ids are numbered
     * in the order the runs are built -- so taking one wall out of the kitchen renumbers
     * the ones after it. Matching on the id would quietly re-tick a sink somebody had just
     * unticked, which is a plumber's bill decided by a counter. A service is the same
     * service if it is the same role on the same wall.
     */
    const was = new Map((scanned_.value?.services ?? [])
      .map((sv) => [`${sv.role}@${sv.wallId}`, scanLocks.value[sv.id]]));
    const found = fromScan(rawScan.value, maker, scanLoop.value.walls, scanLoop.value.origin, { only });
    scanned_.value = found;
    plan.value.runs = found.runs;
    // A service on a wall that is no longer the kitchen has nothing left to lock. One that
    // was never asked about defaults to locked, which is the safe way round.
    scanLocks.value = Object.fromEntries((found.services ?? [])
      .map((sv) => [sv.id, was.get(`${sv.role}@${sv.wallId}`) ?? true]));
  } else if (scanned_.value?.runs) {
    scanned_.value = { ...scanned_.value, runs: scanned_.value.runs.filter((r) => ids.has(r.wallId)) };
    plan.value.runs = scanned_.value.runs;
  }
  kitchen.value?.setWalls(plan.value.walls);
  nudge.value++;
}

/**
 * THE PRIMARY BUTTON ON THE SCANNED CARD, and the only thing it has to decide.
 *
 * Where the server can read a picture there is one more thing worth doing before the
 * generator runs -- a photo of each wall, which is the one thing LiDAR physically cannot
 * see -- so that step goes in front. Where it cannot (which is every server without an
 * image key, including the one this is deployed to today) the step does not exist and the
 * button does exactly what it says.
 *
 * And in front of BOTH of those, on a scan that made a room: which walls are the kitchen.
 * It is first because everything after it is about the walls it chooses -- the photos are
 * per wall, and the generator lays out whatever it is given.
 */
function designFromTheScan() {
  if (flow.value === 'scanned' && (plan.value.walls ?? []).length) { pickWalls(); return; }
  onwardFromWalls();
}

/** Past the walls step: the photos where a server can read one, otherwise the generator. */
function onwardFromWalls() {
  if (flow.value === 'walls') applyPicks();
  if (flow.value !== 'photos' && photoWalls.value.length) { flow.value = 'photos'; return; }
  newDesignFromScan();
}

/** The room, and nothing standing in it. Straight into the options, which is the point. */
function newDesignFromScan() {
  const chosen = fromTheScan({ keepLayout: false });
  if (!chosen) return;
  designForMe();
  const kept = chosen.length
    ? ` ${chosen.length === 1 ? 'The one you ticked stays' : 'The ones you ticked stay'} where the plumbing is.`
    : '';
  say(`A new kitchen for this room.${kept} The scan is still there under Now.`);
}

/** The cabinets as found, as a starting point -- a copy of them, with ids of its own. */
function designFromScanLayout() {
  if (!fromTheScan({ keepLayout: true })) return;
  mode.value = 'room';
  // Past the scan card and into ordinary editing, with the steps still showing the way to
  // Review: there is a kitchen on screen now, it is just theirs rather than the generator's.
  flow.value = 'editing';
  typedWalls.value = true;
  openSheet();
  say('A design that starts where your kitchen is now. The scan itself stays as it was.');
}

/*
 * THE ONE THING A LiDAR SCAN CANNOT SEE.
 *
 * The scanner gives the room and the cabinet RUNS to the millimetre, and every appliance in
 * them as a measured point. What it physically cannot give is where the DOORS AND DRAWERS
 * divide: a door separation is a 3mm reveal on a flat front and an iPhone's depth camera
 * resolves at 10 to 20mm, so a run of four doors and a bank of drawers comes back as one
 * flat plane. Somebody looking at the result sees their kitchen's shape with the wrong
 * cabinets in it, and has to redraw by hand the one part the scan looked like it had done.
 *
 * The divisions are in the CAMERA image. So after a scan, the app offers to photograph each
 * wall that carries cabinets: one photo, sent with what the app already knows about that
 * wall -- its length, the run's span, the appliances the scan measured -- and the reader
 * answers with the SEQUENCE of fronts and their SHARES of the run. Never millimetres; a
 * photograph cannot measure, and shares are the honest limit of what it knows. The
 * millimetres come back on the device, in `divideRun`, snapped onto this maker's range so
 * the run is still exactly full and the appliances have not moved a millimetre.
 *
 * Gated on `features.readImage` like every other reader: a server with no key offers none
 * of this rather than offering a button that answers 503.
 */
const photoWalls = computed(() => {
  nudge.value;
  if (!scanned_.value?.runs?.length || !features.value.readImage) return [];
  const ext = room.extents(plan.value, maker.limits);
  const out = [];
  for (const r of plan.value.runs) {
    // Base runs only: an overhead run is read from the SAME photo as the base run under it,
    // so listing the wall twice would ask for the same picture twice.
    if (r.kind !== 'base' || room.isIsland(plan.value, r.wallId)) continue;
    if (out.some((w) => w.wallId === r.wallId)) continue;
    const e = ext[r.id];
    if (!e || e.to - e.from < room.MIN_RUN) continue;
    out.push({
      wallId: r.wallId,
      name: WALL_NAMES[r.wallId] ?? `The ${r.wallId} wall`,
      mm: Math.round(e.to - e.from),
    });
  }
  return out;
});

/** What the scan measured in one run, in the millimetres the reader is told about. */
function runContext(run, ext) {
  if (!run) return null;
  const e = ext[run.id];
  if (!e || e.to - e.from < room.MIN_RUN) return null;
  const offs = itemOffsets(run.items ?? []);
  const fixed = (run.items ?? []).map((it, i) => {
    const spec = maker.cabinets.find((c) => c.id === it.specId);
    if (!spec?.role || !FIXED_ROLES.includes(spec.role)) return null;
    return {
      role: spec.role,
      atMm: Math.round(e.from + offs[i] + it.w / 2),
      widthMm: Math.round(it.w),
    };
  }).filter(Boolean);
  return { from: Math.round(e.from), to: Math.round(e.to), fixed };
}

/** One front in the words a person would use for it, for the line the app says out loud. */
function frontWord(f) {
  if (f.kind === 'appliance') return `the ${f.role}`;
  if (f.kind === 'drawers') return f.drawers > 1 ? `${f.drawers} drawers` : 'a drawer';
  if (f.kind === 'open') return 'open shelves';
  if (f.kind === 'blank') return 'a panel';
  return f.doors > 1 ? `${f.doors} doors` : 'a door';
}

async function photographWall(w) {
  if (readOnly()) return;
  if (!features.value.readImage) return;
  busy.value = true;
  try {
    const raw = await pickImage();
    if (!raw) { say(`No photo. ${w.name.toLowerCase()} stays as the scan left it.`); return; }
    const small = await shrink(raw);

    // The spans are read HERE rather than remembered from the card, because a person may
    // have dragged a wall or a run since the scan and the reader must be told the wall it
    // is actually looking at.
    const ext = room.extents(plan.value, maker.limits);
    const base = plan.value.runs.find((r) => r.wallId === w.wallId && r.kind === 'base');
    const over = plan.value.runs.find((r) => r.wallId === w.wallId && r.kind === 'wall');
    const seg = room.segment(plan.value, w.wallId);
    if (!seg) { say('That wall is no longer in the room.'); return; }
    const ctx = {
      wall: w.name.toLowerCase(),
      lengthMm: Math.round(room.length(seg)),
      base: runContext(base, ext),
      overhead: runContext(over, ext),
    };
    if (!ctx.base && !ctx.overhead) { say('There is no run on that wall to divide.'); return; }

    const res = await fetch('/api/read-image', {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ image: small.dataUrl, kind: 'run', run: ctx }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      say(e.error ?? 'Could not read that photo.');
      return;
    }
    const r = await res.json();

    rememberRoom();
    // The picture travels with the design whatever the reader made of it: the maker looking
    // at a quote wants to see the wall as it is today.
    const kept = attachImage(plan.value, {
      kind: 'photo', dataUrl: small.dataUrl, bytes: small.bytes,
      note: `${w.name}, for the doors and drawers`,
    });

    const said = [];
    let changed = 0;
    const notes = [...(r.notes ?? [])];
    for (const [run, read] of [[base, r.base], [over, r.overhead]]) {
      if (!run || !read?.fronts?.length) continue;
      const e = ext[run.id];
      if (!e) continue;
      const d = divideRun(run, read.fronts, maker,
        { from: e.from, to: e.to, confidence: r.confidence ?? 0 });
      notes.push(...d.notes);
      if (d.items === run.items) continue;
      run.items = d.items;
      changed += 1;
      if (run.kind === 'base') said.push(...read.fronts.map(frontWord));
    }
    if (!changed) {
      say(notes[0] ?? 'Nothing in that photo could be made out. The wall is as the scan left it.');
      return;
    }
    kitchen.value.setPlan(plan.value);
    nudge.value++;
    const line = said.length ? `${w.name}: ${said.join(', ')} — from your photo.`
      : `${w.name} redivided from your photo.`;
    const caveat = r.confidence < 0.6 && notes.length ? ` ${notes[0]}` : '';
    say(`${line}${caveat}${kept.ok ? '' : ` ${kept.why}`}`);
  } catch (e) {
    say(String(e?.message ?? e));
  } finally {
    busy.value = false;
  }
}

const readRoom = ref(null);
async function readImage(image, kind) {
  if (!features.value.readImage) return;
  const res = await fetch('/api/read-image', {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ image: image.dataUrl, kind }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); say(e.error ?? 'Could not read that picture.'); return; }
  const r = await res.json();
  if (kind === 'render') { applyMatch(r); return; }
  // A plan or a photo: the room, shown before it is applied.
  r.walls = (r.walls ?? []).map((w) => ({ ...w, mm: Math.round(w.mm / 10) * 10 }));
  // The reader has always returned the doors and windows it could see, and the app has
  // always thrown them away. Kept now, and applied with the walls.
  r.openings = (r.openings ?? []).filter((o) => o && Number.isFinite(o.at) && o.width > 0);
  if (!r.walls.length) { say(r.notes?.[0] ?? 'Could not make out the walls in that picture.'); return; }
  readRoom.value = r;
  mode.value = 'room';
  openSheet();
}

/**
 * A kitchen they like, matched to this maker's range.
 *
 * The reader picks the nearest door style, colour, benchtop, splashback and handle from the
 * lists it was given; each is applied through `pick`, the same as a tap on a swatch. What
 * the picture has that the range does not is said, not hidden.
 */
function applyMatch(m) {
  rememberRoom();
  const applied = [];
  for (const f of FINISHES) {
    const id = m[f.id];
    const opt = maker[f.list].find((o) => o.id === id);
    if (opt) { plan.value[f.id] = id; applied.push(opt.name); }
  }
  kitchen.value.setPlan(plan.value);
  nudge.value++;
  mode.value = 'finish';
  openSheet();
  const missing = m.notes?.length ? ` ${m.notes[0]}` : '';
  say(`Matched: ${applied.join(', ')}.${missing}`);
}

function applyRead() {
  if (readOnly()) return;
  const r = readRoom.value;
  if (!r) return;
  rememberRoom();
  const byLabel = (re) => r.walls.find((w) => re.test(w.label))?.mm;
  const width = byLabel(/back|front|top|bottom|north|south/i) ?? r.walls[0]?.mm ?? 4200;
  const depth = byLabel(/left|right|side|east|west/i) ?? r.walls[1]?.mm ?? width;
  const shape = ['single', 'galley', 'l', 'u'].includes(r.shape) ? r.shape : 'l';
  const walls = room.preset(shape, { width: clampRoom(width, 4200), depth: clampRoom(depth, 3600) });
  const wanted = room.presetRuns(shape);
  plan.value.shape = shape;
  plan.value.runs = wanted.map(([wallId, kind]) => ({ id: `r-${wallId}-${kind}-${Date.now()}`, wallId, kind, items: [] }));
  /*
   * The reader names a wall by its LABEL ("back wall", "north"), not by an id the planner
   * knows, so each opening is matched onto the wall whose own label it reads like and
   * dropped when nothing matches. A doorway on the wrong wall is worse than none.
   */
  plan.value.openings = (r.openings ?? []).map((o, i) => {
    const wallId = walls.map((w) => w.id).find((id) => new RegExp(id, 'i').test(String(o.wall ?? '')))
      ?? (/back|top|north/i.test(String(o.wall ?? '')) ? 'back'
        : /front|bottom|south/i.test(String(o.wall ?? '')) ? 'front'
          : /left|west/i.test(String(o.wall ?? '')) ? 'left'
            : /right|east/i.test(String(o.wall ?? '')) ? 'right' : null);
    if (!wallId || !walls.some((w) => w.id === wallId)) return null;
    const kind = o.kind === 'window' ? 'window' : 'door';
    const width = Math.round(Math.min(Math.max(o.width || 870, 400), 3000) / 10) * 10;
    const L = room.length(walls.find((w) => w.id === wallId));
    return { id: `o-${i}`, wallId, kind, width,
      at: Math.max(0, Math.min(Math.round((o.at ?? 0) / 10) * 10, L - width)),
      ...room.OPENING[kind] };
  }).filter(Boolean);
  kitchen.value.setWalls(walls);
  readRoom.value = null;
  close();
  nudge.value++;
  // The point of reading the picture was to design the kitchen: go straight to the options.
  designForMe(1);
  say(`${shape === 'l' ? 'An L' : shape === 'u' ? 'A U' : shape === 'galley' ? 'A galley' : 'One wall'}, ${width} by ${depth}. Three kitchens for it below; check the numbers against a tape.`);
}

/**
 * The page's operations, for the assistant.
 *
 * Every tool the model may call maps to one of these, and every one of these is what a tap
 * already does -- so the assistant can do exactly what a finger can. Each returns
 * { ok, why? } in the maker's words.
 */
const ops = {
  remember: () => rememberRoom(),
  changed: () => { nudge.value++; },
  add: (specId, wallId, near) => {
    const spec = maker.cabinets.find((c) => c.id === specId);
    if (!spec) return { ok: false, why: 'not in this range' };
    const wantKind = spec.kind === 'wall' ? 'wall' : 'base';
    const run = plan.value.runs.find((r) => r.kind === wantKind && (!wallId || r.wallId === wallId))
      ?? plan.value.runs.find((r) => r.kind === wantKind);
    if (!run) return { ok: false, why: `no ${wantKind === 'wall' ? 'overhead' : 'base'} run on that wall` };
    let at = null;
    if (near === 'start') at = 0;
    else if (['sink', 'cooktop', 'fridge', 'dishwasher', 'oven'].includes(near)) {
      const i = run.items.findIndex((it) => maker.cabinets.find((c) => c.id === it.specId)?.role === near);
      if (i >= 0) at = i + 1;
    }
    const before = run.items.map((i) => i.id);
    const r = kitchen.value.add(run.id, specId, at);
    if (!r.ok) return r;
    const itemId = run.items.map((i) => i.id).find((id) => !before.includes(id));
    return { ok: true, itemId };
  },
  remove: (run, item) => kitchen.value.remove(run.id, item.id),
  swap: (run, item, specId) => {
    selected.value = item; selectedRun.value = run;
    swap(specId);
    const ok = run.items.some((i) => i.id === item.id && i.specId === specId);
    close();
    return ok ? { ok: true } : { ok: false, why: 'that will not fit without changing something else' };
  },
  setWidth: (run, item, mm) => {
    selected.value = item; selectedRun.value = run;
    setWidth(mm);
    const now = run.items.find((i) => i.id === item.id)?.w;
    close();
    return Math.abs(now - mm) < 0.5 ? { ok: true } : { ok: false, why: 'the neighbours cannot make that room' };
  },
  move: (run, item, wallId, index) => {
    const to = plan.value.runs.find((r) => r.wallId === wallId && r.kind === run.kind) ?? run;
    if (to !== run) {
      const x = to.items.slice(0, index).reduce((n, i) => n + i.w, 0);
      const r = kitchen.value.moveAcross(item.id, run, to, x);
      return r.ok ? { ok: true } : r;
    }
    const i = run.items.findIndex((it) => it.id === item.id);
    const x = run.items.filter((_, k) => k !== i).slice(0, index).reduce((n, it) => n + it.w, 0);
    run.items = slideItems(run.items, i, x + 1);
    kitchen.value.buildRuns();
    return { ok: true };
  },
  setWall: (wallId, mm) => {
    const w = plan.value.walls.find((x) => x.id === wallId);
    if (!w) return { ok: false, why: `no wall called ${wallId}` };
    if (mm < ROOM.min || mm > ROOM.max) return { ok: false, why: `walls are between ${ROOM.min} and ${ROOM.max}mm here` };
    kitchen.value.setWallLength(wallId, mm);
    return { ok: true };
  },
  setShape: (shape) => setShape(shape),
  toggleWall: (wallId) => toggleWall(wallId),
  pick: (field, id) => pick(field, id),
  lock: (item, locked) => {
    plan.value.locks = (plan.value.locks ?? []).filter((l) => l.itemId !== item.id);
    if (locked) plan.value.locks.push({ itemId: item.id, role: maker.cabinets.find((c) => c.id === item.specId)?.role });
  },
  designOptions: () => { designed.value = designOrFill(plan.value, maker, { seed: designSeed.value, count: 3 }); return designed.value; },
  chooseOption: (id) => {
    const o = designed.value.options.find((x) => x.id === id);
    if (!o) return { ok: false, why: 'no such option; call design_options first' };
    chooseOption(o);
    return { ok: true, name: o.name };
  },
};

/** Locks: the cabinets that stay where they are. Stored on the plan, so they travel with it. */
const lockable = computed(() => LOCKABLE.includes(selectedSpec.value?.role));
const isLocked = computed(() => (plan.value.locks ?? []).some((l) => l.itemId === selected.value?.id));
function toggleLock() {
  if (readOnly()) return;
  const item = selected.value;
  if (!item) return;
  plan.value.locks = plan.value.locks ?? [];
  if (isLocked.value) plan.value.locks = plan.value.locks.filter((l) => l.itemId !== item.id);
  else plan.value.locks.push({ itemId: item.id, role: selectedSpec.value.role });
  nudge.value++;
}

/**
 * Design it for me.
 *
 * Three kitchens from the generator, for this room, keeping whatever is locked. Shown in the
 * sheet as options; choosing one is a single undo step. `designSeed` walks down each taste's
 * list for "show me others".
 */
const designed = ref({ options: [], rejected: [] });
const designSeed = ref(1);
function designForMe(seed = 1) {
  if (readOnly()) return;
  designSeed.value = seed;
  /*
   * `designOrFill`, NOT `design`.
   *
   * The generator returns an empty list when the locks cannot all be honoured, and an empty
   * list reaches a person as "Nothing fits with the cabinets you have locked where they
   * are" over a room with one cabinet in it -- which is what the owner got after ticking the
   * oven, the cooktop and the dishwasher on his own scan. `designOrFill` lets one lock go at
   * a time and, failing that, fills the runs, so this step always has something on the other
   * side of it. `note` is its one sentence about what it had to give up; it is said out loud
   * rather than buried, because a design with the oven moved is only a good answer if the
   * person is told the oven moved.
   */
  designed.value = designOrFill(plan.value, maker, { seed, count: 3 });
  chosenOption.value = null;
  if (designed.value.note) say(designed.value.note);
  close();
  mode.value = 'options';
  // A design asked for from anywhere in the guided flow IS step 2 arriving; asking for
  // another three (`Show me others`) leaves the step where it was. 'problem' is in the list
  // because typing the walls in is the way ON from a scan that would not become a room, and
  // a failure card standing over three finished kitchens is a card about nothing.
  if (['start', 'scanned', 'walls', 'problem', 'photos'].includes(flow.value)) flow.value = 'design';
  openSheet();
  nudge.value++;
}
function chooseOption(o) {
  if (readOnly()) return;
  rememberRoom();
  plan.value.runs = o.plan.runs.map((r) => ({ ...r, items: r.items.map((i) => ({ ...i })) }));
  plan.value.locks = o.plan.locks ?? [];
  kitchen.value.setPlan(plan.value);
  close();
  chosenOption.value = o.id;
  /*
   * WHERE A CHOSEN KITCHEN LANDS: on the cabinets, not on the review.
   *
   * Review is the last step and it opens with a paragraph of counts and a row of export
   * buttons -- which is the right screen for somebody who is finished and the wrong one for
   * somebody who has had a kitchen for four seconds. Cabinets is the first thing they can
   * usefully change, so that is where the sheet goes, once, with a line saying so.
   */
  if (flow.value) {
    flow.value = 'editing';
    mode.value = 'cabinets';
    nudge.value++;
    /*
     * ONE PILL, NOT TWO. The hint and the refusal are separate elements at the same place on
     * screen, so firing both at once stacks them and the top one is unreadable -- which is
     * what happened the first time this said its piece and named the kitchen in the same
     * breath. The first time through, the hint says everything; after that, the short line.
     */
    if (!prefs.seenChosenHint) {
      prefs.seenChosenHint = true;
      hint.value = `${o.name}. Tap any cabinet in it to change it, or go on to Finishes. `
        + 'Undo brings the old one back.';
      window.setTimeout(() => { if (hint.value?.startsWith(o.name)) hint.value = null; }, 7000);
    } else {
      say(`${o.name}. Undo brings the old one back.`);
    }
    return;
  }
  mode.value = 'review';
  nudge.value++;
  say(`${o.name}. Undo brings the old one back.`);
}

const canResize = computed(() =>
  selectedSpec.value && selectedSpec.value.stretch[1] > selectedSpec.value.stretch[0]);

/** Never offer a width the maker will not cut. The slider stops; it does not warn. */
const widthCeiling = computed(() => Math.min(
  selectedSpec.value?.stretch[1] ?? 0, maker.limits.maxCabinetWidth));

const problems = computed(() => {
  nudge.value;
  const out = [];
  for (const run of plan.value.runs) out.push(...faults(run, maker.cabinets, maker.limits));
  return out;
});

const sched = computed(() => { nudge.value; return schedule(plan.value, maker); });
/** What it might cost, from the maker's bands; null for a maker who has not said. */
const est = computed(() => { nudge.value; return estimate(plan.value, maker); });

/** A finding's one-tap fix: the same swap a finger makes, with the same refusal if it will not fit. */
function applyFix(f) {
  if (readOnly()) return;
  const fix = fixFor(f, maker);
  if (!fix) return;
  const run = plan.value.runs.find((r) => r.id === fix.runId);
  const item = run?.items.find((i) => i.id === fix.itemId);
  if (!run || !item) return;
  selected.value = item; selectedRun.value = run;
  swap(fix.specId);
  const done = run.items.some((i) => i.id === item.id && i.specId === fix.specId);
  close();
  if (done) say(`Done. ${maker.cabinets.find((c) => c.id === fix.specId)?.name} is in. Undo brings the old one back.`);
}

/** The designer's review, re-run on every change so the list is never stale. */
const findings = computed(() => { nudge.value; return review(plan.value, maker); });
// `faults` is already the name of layout.js's arithmetic check, imported above.
const mustFix = computed(() => findings.value.filter((f) => f.level === 'fault'));

/** Tap a finding and the cabinet it is about comes up in the sheet. */
function goTo(f) {
  if (!f.itemId) return;
  const run = plan.value.runs.find((r) => r.id === f.runId);
  const item = run?.items.find((i) => i.id === f.itemId);
  if (!run || !item) return;
  selected.value = item;
  selectedRun.value = run;
}

onMounted(() => {
  // Before the scene, so the page is already wearing the maker's colours on first paint
  // rather than flashing ours and then correcting itself.
  applyBrand(maker);

  const k = new Kitchen(canvas.value, maker);
  kitchen.value = k;
  k.setPlan(plan.value);

  /*
   * Which kitchen to open.
   *
   * A link's kitchen wins, then the one autosaved on this device, then the starter -- and
   * the starter is DESIGNED before anyone sees it, by the same generator as the button, so
   * the first Review a person reads is advice and not a list of what is wrong with our demo.
   */
  (async () => {
    /*
     * A hosted share: ?s=<id>, the package fetched from the server. Its Original, if it
     * carried one, is held in memory only -- this device never scanned that room -- and
     * `sharedRoomUrl` is where its USDZ can be looked at. The plan is taken LAST, because
     * taking it fires the watch on `originalId`, which keeps an in-memory Original whose
     * id matches and would otherwise replace it with nothing.
     */
    const sid = idFromSearch(window.location.search);
    if (sid) {
      try {
        const got = await openShared(sid);
        try { window.history.replaceState(null, '', window.location.pathname); } catch { /* fine */ }
        if (got.original) original.value = got.original;
        sharedRoomUrl.value = got.roomUrl;
        // Somebody else's finished kitchen: not a journey, so no stepper over it.
        flow.value = null;
        takePlan(got.design, { remember: false });
        say(got.original
          ? 'Opened a shared kitchen. The room it was designed for is under Now.'
          : 'Opened a shared kitchen. It is yours to change; the link stays as it was.');
        return;
      } catch (e) { say(`Could not open that share: ${e?.message ?? e}`); }
    }
    const code = codeFromHash(window.location.hash);
    const shared = code ? await decodeShare(code, maker) : null;
    if (shared) {
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch { /* fine */ }
      flow.value = null;
      takePlan(shared, { remember: false });
      say('Opened a shared kitchen. It is yours to change; the link stays as it was.');
      return;
    }
    /*
     * The kitchen this device was last working on. It has a room and cabinets already, so
     * the person is mid-edit, not mid-journey: no stepper, and the Room tab as it always was.
     *
     * BUT ONLY IF THEY HAVE NOT ALREADY STARTED. `loadCurrent` reads IndexedDB, which on a
     * cold start can take longer than it takes somebody to press the one button on screen --
     * and this used to hand the plan to `takePlan` regardless, throwing away whatever they
     * had just done. The window is milliseconds and a scan is a minute of walking around
     * their kitchen, which is exactly the wrong thing to lose to a race.
     */
    const saved = await loadCurrent();
    if (saved && !scanned_.value && flow.value === 'start' && mode.value === 'room') {
      flow.value = null;
      takePlan(saved, { remember: false });
      return;
    }
    if (saved) return;
    const d = designOrFill(plan.value, maker, { seed: 1, count: 1 });
    if (d.options?.[0]) {
      plan.value.runs = d.options[0].plan.runs.map((r) => ({ ...r, items: r.items.map((i) => ({ ...i })) }));
      k.setPlan(plan.value);
      nudge.value++;
    }
    if (!prefs.seenPlannerHint) {
      prefs.seenPlannerHint = true;
      hint.value = 'Tap a cabinet to change it. Drag it to move it. Drag the handle down to see more.';
      window.setTimeout(() => { if (hint.value?.startsWith('Tap a cabinet')) hint.value = null; }, 6000);
    }
  })();

  // Kept as you go. Debounced: a drag is fifty changes and one save.
  let saveTimer = null;
  watch(nudge, () => {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => saveCurrent(plan.value), 700);
  });

  attach(k, {
    haptic: () => { try { navigator.vibrate?.(8); } catch { /* desktop */ } },
    // Showing the room as scanned, every cabinet in it is locked, and touch.js says what it
    // always says about a locked cabinet -- "tap it to unlock" -- which is not true here and
    // not the reason. The one sentence that IS the reason replaces it.
    onHint: (why) => {
      hint.value = viewing.value === 'now'
        ? 'This is the room as scanned. Switch back to New to change anything.' : why;
    },
    onSelect: (item, run) => {
      if (readOnly()) return;
      selected.value = item; selectedRun.value = run; openSheet();
    },
    onCommit: (run, before) => {
      if (readOnly()) return;
      history.value.push({ runId: run.id, items: before }); nudge.value++;
    },
    // A drag that goes round a corner touches two runs; remember the room once, before it does.
    onCross: () => { if (!readOnly()) rememberRoom(); },
  });

  // Tapping the room, rather than a cabinet, puts the whole-kitchen sheet back.
  k.onPick = () => {};
  canvas.value.addEventListener('pointerdown', (e) => {
    if (!k.at(e.clientX, e.clientY)) close();
  });

  // Tell the camera how much of the window the sheet is standing on, and keep telling it:
  // the sheet grows and shrinks as its content changes.
  /*
   * Tell the camera how much of the window is covered, and keep telling it.
   *
   * Not the sheet's height: the height still SHOWING, plus the bar under it. A sheet dragged
   * down uncovers the kitchen, and the camera should use the room it just got.
   */
  /*
   * Tell the camera how much of the window is covered, and keep telling it.
   *
   * Not the sheet's height: the height still SHOWING, plus the bar under it. A sheet dragged
   * down uncovers the kitchen, and the camera should use the room it just got.
   *
   * The bar is measured by the same observer rather than once on mount -- an Ionic page is
   * laid out after its component mounts, so a height read here is 0 and the camera spent the
   * session believing the bottom 100px of the window was free.
   */
  const tellCamera = () => {
    if (Math.abs(covered.value - k.inset) < 4) return;
    k.inset = covered.value;
    if (!k.touched) k.frame();
  };
  // offsetHeight, not contentRect: contentRect excludes padding, and both of these carry
  // padding for the home indicator -- which is 32px of the window the camera would have
  // thought was free.
  const chrome = new ResizeObserver((entries) => {
    for (const e of entries) {
      if (e.target === sheetEl.value) sheetH.value = e.target.offsetHeight;
      else if (e.target === brandEl.value) topH.value = e.target.offsetHeight;
      else barH.value = e.target.offsetHeight;
    }
    tellCamera();
  });
  const watchChrome = () => {
    chrome.disconnect();
    if (sheetEl.value) chrome.observe(sheetEl.value);
    if (brandEl.value) chrome.observe(brandEl.value);
    const bar = document.querySelector('.bar');
    if (bar) chrome.observe(bar);
  };
  // On a wide screen the sheet stands beside the kitchen, not on it.
  const wide = window.matchMedia('(min-width: 900px)');
  docked.value = wide.matches;
  wide.addEventListener?.('change', (e) => { docked.value = e.matches; y.value = 0; tellCamera(); });
  watchChrome();
  window.setTimeout(watchChrome, 300);
  // The sheet element is replaced when the cabinet sheet takes over from the mode sheet.
  watch(sheetEl, watchChrome);
  watch(y, tellCamera);

  window.addEventListener('resize', onWindowResize);
  window.__kitchen = k;

  /*
   * ── A WAY TO STAND IN THE MIDDLE OF THE FLOW WITHOUT A LiDAR PHONE ─────────────────
   *
   * Development builds only (`import.meta.env.DEV`), so nothing of this is in `dist/`.
   *
   * The half of the guided flow that matters most is the half a laptop can never reach:
   * everything after a scan. `fakeScan()` writes a room that mimics the owner's kitchen --
   * four walls, a doorway, three windows, two openings, twelve cupboards, the oven,
   * cooktop, sink and dishwasher, and an island -- through the SAME `saveOriginal` a real
   * scan uses, so the buttons on the card do the real thing rather than a mimed version of
   * it. `{ photos: true }` also turns on the image reader, which is what puts the
   * Photograph-each-wall step between Scanned and Design.
   *
   *   await window.__planner.fakeScan()
   *   await window.__planner.fakeScan({ photos: true })
   *   window.__planner.flow.value            // 'start' | 'scanned' | 'problem' | 'photos' | …
   *
   * And the other half of a scan, which is the half that goes wrong. `failScan(kind)` drives
   * the three ways `runTheScan` can fail, through `runTheScan` itself with a payload shaped
   * like a real one, so what comes up is the real card and the real report rather than a
   * mimed version of either:
   *
   *   await window.__planner.failScan('cancelled')   // no card, no pill, Start again
   *   await window.__planner.failScan('error')       // the scanner itself threw
   *   await window.__planner.failScan('unclosed')    // walls that will not make a room
   *   window.__planner.scanReport()                  // what Copy the scan report copies
   *
   * And the one scan that matters most, which is a real one. `loadFixture()` reads a saved
   * "Copy what the scan saw" paste -- the owner's own open-plan floor, twenty walls, forty
   * objects -- through the SAME `runTheScan` a phone goes through, so what comes up is the
   * real room, the real card, the real notes and the real wall-picking step with the real
   * guess already ticked. It is the only way to see that step's default from a laptop:
   *
   *   await window.__planner.loadFixture()           // stands on the scanned card
   *   await window.__planner.loadFixture({ walls: true })   // straight into the walls step
   */
  if (import.meta.env.DEV) {
    window.__planner = {
      // `scan` is here so the Start card can be seen as an iPhone Pro sees it -- the hero
      // and its one quiet alternative -- from a laptop that has no LiDAR to offer:
      //   window.__planner.scan.value = { ok: true, reason: null, platform: 'ios' }
      flow, mode, plan, designed, features, scan, scanned_, scanLocks, typedWalls,
      rawScan, scanProblem, scanLoop, scanReport,
      failScan(kind = 'unclosed') {
        return runTheScan(async () => {
          if (kind === 'cancelled') return null;
          if (kind === 'error') {
            throw new Error('The scan could not be processed: ARSession failed (sensor unavailable).');
          }
          /*
           * A ROOM THAT WILL NOT CLOSE, in the ordinary way rooms do not close: two walls
           * measured properly, a stub of a third that is under `MIN_WALL` and gets dropped,
           * and nothing at all where the rest of the kitchen was -- somebody turned round
           * half way. Everything else is what the owner's scan came back with: the doorway,
           * two windows, the benchtops, and the appliances RoomPlan recognised. All of it
           * has to survive the failure, which is what the card and the report are for.
           */
          const seg = (cx, cz, dx, dz, length, extra = {}) =>
            ({ cx, cy: 1200, cz, dx, dz, length, height: 2400, confidence: 'high', ...extra });
          return {
            walls: [
              seg(0, -1800, 1, 0, 4200),
              seg(2100, 0, 0, 1, 3600),
              seg(-2100, -1700, 0, 1, 150),
            ],
            doors: [seg(-2100, 400, 0, 1, 870, { cy: 1020, height: 2040 })],
            windows: [
              seg(-400, -1800, 1, 0, 1200, { cy: 1500, height: 1000 }),
              seg(2100, -600, 0, 1, 900, { cy: 1500, height: 1000 }),
            ],
            openings: [],
            objects: [
              { cx: -900, cy: 450, cz: -1500, dx: 1, dz: 0, length: 2400, height: 900,
                depth: 600, category: 'storage', confidence: 'medium', storageType: 'cabinet' },
              { cx: 1500, cy: 450, cz: -1500, dx: 1, dz: 0, length: 1200, height: 900,
                depth: 600, category: 'storage', confidence: 'low', storageType: 'cabinet' },
              { cx: 300, cy: 450, cz: -1500, dx: 1, dz: 0, length: 600, height: 900,
                depth: 600, category: 'sink', confidence: 'high', storageType: null },
              { cx: 1100, cy: 450, cz: -1500, dx: 1, dz: 0, length: 600, height: 900,
                depth: 600, category: 'stove', confidence: 'high', storageType: null },
              { cx: 1800, cy: 450, cz: -1500, dx: 1, dz: 0, length: 600, height: 900,
                depth: 600, category: 'dishwasher', confidence: 'medium', storageType: null },
              { cx: 1900, cy: 900, cz: 900, dx: 0, dz: 1, length: 900, height: 1800,
                depth: 700, category: 'refrigerator', confidence: 'high', storageType: null },
            ],
            benches: [{ cx: 600, cz: -1500, dx: 1, dz: 0, length: 3600, depth: 600, y: 900 }],
            fronts: [{ cx: 600, cz: -1720, dx: 1, dz: 0, length: 2400, depth: 350, y0: 1500, y1: 2100 }],
            modelPath: 'file:///dev/as-scanned.usdz',
            meshPath: 'file:///dev/as-scanned-mesh.usdz',
            parametricPath: 'file:///dev/as-scanned-parametric.usdz',
          };
        });
      },
      /*
       * A REAL SCAN, OFF DISK, THROUGH THE REAL PATH.
       *
       * The fixture is the plugin's own report format and `parseScan` is the same reader the
       * tests use, so nothing here mimes anything: `runTheScan` gets a payload, the loop
       * builds the room from it, `fromScan` reproduces the kitchen, the Original is written.
       *
       * The paste is already in the PLANNER's frame (the room squared up and re-origined),
       * which `loopFromSegments` would do a second time -- so the walls go in as segments
       * whose midpoints and directions are the fixture's own, and come back where they were.
       */
      async loadFixture({ file = 'harbourside-open-plan', walls = false } = {}) {
        const [{ parseScan }, text] = await Promise.all([
          import('../../scripts/scan-fixture.mjs'),
          fetch(`/src/kitchen/fixtures/${file}.txt`).then((r) => r.text()),
        ]);
        const f = parseScan(text);
        const seg = (w) => ({
          cx: (w.x1 + w.x2) / 2, cz: (w.y1 + w.y2) / 2, cy: 1200,
          dx: (w.x2 - w.x1) / (Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1),
          dz: (w.y2 - w.y1) / (Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1),
          length: Math.hypot(w.x2 - w.x1, w.y2 - w.y1), height: 2400, confidence: 'high',
        });
        await runTheScan(async () => ({
          ...f.scan, walls: f.walls.map(seg), modelPath: null,
        }));
        if (walls && flow.value === 'scanned') designFromTheScan();
        return { walls: f.walls.length, objects: f.scan.objects.length };
      },
      async fakeScan({ photos = false } = {}) {
        if (photos) features.value = { ...features.value, readImage: true };
        const walls = room.preset('u', { width: 4200, depth: 4200 });
        const base = plan.value.runs.find((r) => r.kind === 'base') ?? plan.value.runs[0];
        // Everything else empty, so the counts in the card are the fixture's and not the
        // starter kitchen's left over underneath it.
        for (const r of plan.value.runs) r.items = [];
        const spec = (role) => maker.cabinets.find((c) => c.role === role)?.id;
        const plain = maker.cabinets.find((c) => c.kind === 'base' && !c.role)?.id;
        const services = [
          { id: 'fx-oven', role: 'oven', wallId: base?.wallId ?? 'back' },
          { id: 'fx-cooktop', role: 'cooktop', wallId: base?.wallId ?? 'back' },
          { id: 'fx-sink', role: 'sink', wallId: base?.wallId ?? 'back' },
          { id: 'fx-dishwasher', role: 'dishwasher', wallId: base?.wallId ?? 'back' },
        ];
        const items = [
          ...Array.from({ length: 12 }, (_, i) => ({ id: `fx-c${i}`, specId: plain, w: 600 })),
          ...services.map((sv) => ({ id: sv.id, specId: spec(sv.role) ?? plain, w: 600 })),
        ];
        plan.value.shape = 'u';
        plan.value.openings = [
          { id: 'fx-d1', wallId: 'left', kind: 'door', at: 300, width: 870, ...room.OPENING.door },
          { id: 'fx-w1', wallId: 'back', kind: 'window', at: 900, width: 1200, ...room.OPENING.window },
          { id: 'fx-w2', wallId: 'right', kind: 'window', at: 600, width: 900, ...room.OPENING.window },
          { id: 'fx-w3', wallId: 'front', kind: 'window', at: 1500, width: 900, ...room.OPENING.window },
        ];
        const bb = room.bounds(walls);
        plan.value.islands = [room.island(
          { x: (bb.minX + bb.maxX) / 2, y: (bb.minY + bb.maxY) / 2 }, 0, 2400, { id: 'fx-isle' })];
        if (base) base.items = items;
        k.setWalls(walls);
        const found = {
          runs: plan.value.runs, islands: plan.value.islands, openings: plan.value.openings,
          services, notes: ['Two cupboards came back low-confidence and were placed anyway.'],
          saw: { walls: 4, doors: 1, windows: 3, openings: 2, objects: 20, kinds: {},
            unknown: [], usedOpenings: 4, benches: 2, fronts: 3, things: [] },
        };
        original.value = await saveOriginal(plan.value, found,
          { name: 'As scanned', modelPath: null });
        scanned_.value = found;
        scanLocks.value = Object.fromEntries(services.map((sv) => [sv.id, true]));
        keepIslandInDesign.value = true;
        flow.value = 'scanned';
        typedWalls.value = false;
        mode.value = 'room';
        openSheet();
        nudge.value++;
        return found;
      },
    };
  }
});

onBeforeUnmount(() => window.removeEventListener('resize', onWindowResize));
function onWindowResize() { kitchen.value?.resize(); }
function close() { selected.value = null; selectedRun.value = null; }

// The scene draws the highlight; the page decides what is selected. Watched rather than set
// at each call site, because a cabinet gets picked from three places -- a tap in 3D, a tap on
// the plan, and a tap on a finding -- and one of them will always be the one that forgets.
watch(selected, (item) => kitchen.value?.select(item?.id ?? null));
function onSelect(item, run) {
  if (readOnly()) return; selected.value = item; selectedRun.value = run; openSheet(); }
function planHint(text) { hint.value = text; }

/** The plan asks before it changes something: a whole-room step, or one run's arrangement. */
function rememberFor(runId) {
  if (!runId) { rememberRoom(); return; }
  const run = plan.value.runs.find((r) => r.id === runId);
  if (run) remember(run);
}
/** A cabinet drag on the plan has finished; the 3D drag path records the same way. */
function committed() { nudge.value++; }

/**
 * An island: a run of cabinets standing on nothing but the floor.
 *
 * Placed across the middle of the room, facing the same way as the longest run that already
 * exists, at 2400 or whatever the room allows. Everything after that is dragging.
 */
function addIsland() {
  if (readOnly()) return;
  rememberRoom();
  const bb = room.bounds(plan.value.walls);
  const centre = { x: (bb.minX + bb.maxX) / 2, y: (bb.minY + bb.maxY) / 2 };
  // Along the room's longer axis, which is the way an island nearly always goes.
  const wide = bb.maxX - bb.minX >= bb.maxY - bb.minY;
  const len = Math.min(2400, Math.max(1200, (wide ? bb.maxX - bb.minX : bb.maxY - bb.minY) - 2400));
  const isle = room.island(centre, wide ? 0 : Math.PI / 2, len, { id: `island-${Date.now()}` });
  plan.value.islands = [isle];
  plan.value.runs = [...plan.value.runs,
    { id: `r-${isle.id}-base-${Date.now()}`, wallId: isle.id, kind: 'base', items: [] }];
  kitchen.value.setIslands(plan.value.islands);
  close();
  nudge.value++;
  view.value = 'plan';
  say('Island added. Drag it to move it, and the dot off its end to turn it.');
}

/** The island's own numbers, for the fields that show them. */
const theIsland = computed(() => plan.value.islands?.[0] ?? null);
const islandLength = computed(() => (theIsland.value ? Math.round(room.length(theIsland.value)) : 0));
const islandOverhang = computed(() => Math.round(theIsland.value?.overhang ?? 0));
/*
 * The angle, as a person reads one: 0 is along the room's x axis and it counts
 * ANTICLOCKWISE, because that is what a protractor does. Screen y is down, which is why
 * the sign is flipped here and again in room.spinSegment -- the two must agree or the
 * arrows turn the island the wrong way.
 */
const islandAngle = computed(() => {
  const s = theIsland.value;
  if (!s) return 0;
  const deg = Math.round(-Math.atan2(s.y2 - s.y1, s.x2 - s.x1) * 180 / Math.PI);
  return ((deg % 360) + 360) % 360;
});

/** Keep the centre, change the length. Growing it from one end would walk it across the room. */
function setIslandLength(mm) {
  if (readOnly()) return;
  const s = theIsland.value;
  if (!s || !Number.isFinite(mm)) return;
  const len = Math.max(900, Math.min(4800, Math.round(mm / 10) * 10));
  rememberRoom();
  const c = room.middle(s);
  const a = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
  plan.value.islands = [{ ...s,
    x1: Math.round(c.x - Math.cos(a) * len / 2), y1: Math.round(c.y - Math.sin(a) * len / 2),
    x2: Math.round(c.x + Math.cos(a) * len / 2), y2: Math.round(c.y + Math.sin(a) * len / 2) }];
  kitchen.value.setIslands(plan.value.islands);
  nudge.value++;
}

function setIslandOverhang(mm) {
  if (readOnly()) return;
  const s = theIsland.value;
  if (!s || !Number.isFinite(mm)) return;
  rememberRoom();
  plan.value.islands = [{ ...s, overhang: Math.max(0, Math.min(600, Math.round(mm / 25) * 25)) }];
  kitchen.value.setIslands(plan.value.islands);
  nudge.value++;
}

/** Turn it about its own centre, in the same 15-degree steps the plan's drag handle uses. */
function spinIsland(deg) {
  if (readOnly()) return;
  const s = theIsland.value;
  if (!s) return;
  rememberRoom();
  plan.value.islands = [room.spinSegment(s, deg)];
  kitchen.value.setIslands(plan.value.islands);
  nudge.value++;
}

/** Swap its ends: the cabinets face the other way and the seating follows them. */
function flipIsland() {
  if (readOnly()) return;
  rememberRoom();
  plan.value.islands = plan.value.islands.map((s) => room.flipSegment(s));
  kitchen.value.setIslands(plan.value.islands);
  nudge.value++;
  say('Turned round. The cabinets face the other way.');
}

function dropIsland() {
  if (readOnly()) return;
  rememberRoom();
  const ids = new Set((plan.value.islands ?? []).map((s) => s.id));
  plan.value.runs = plan.value.runs.filter((r) => !ids.has(r.wallId));
  plan.value.islands = [];
  kitchen.value.setIslands([]);
  close();
  nudge.value++;
}

/** Tap a wall on the plan: cabinets on if it has none, off if it has. */
function toggleWall(wallId) {
  if (readOnly()) return;
  rememberRoom();
  const has = plan.value.runs.some((r) => r.wallId === wallId);
  if (has) {
    plan.value.runs = plan.value.runs.filter((r) => r.wallId !== wallId);
  } else {
    const stamp = Date.now();
    plan.value.runs.push(
      { id: `r-${wallId}-base-${stamp}`, wallId, kind: 'base', items: [] },
      { id: `r-${wallId}-wall-${stamp}`, wallId, kind: 'wall', items: [] },
    );
  }
  plan.value.shape = null;
  kitchen.value.setWalls(plan.value.walls);
  close();
  nudge.value++;
}

function say(why) {
  refusal.value = why;
  window.setTimeout(() => { if (refusal.value === why) refusal.value = null; }, 3600);
}

function remember(run) {
  future.value = [];
  history.value.push({ runId: run.id, items: run.items.map((i) => ({ ...i })) });
}

/** Everything Undo could put back, as one picture of the room. Redo is always one of these. */
function snapshotRoom() {
  const p = plan.value;
  return {
    walls: p.walls.map((w) => ({ ...w })), shape: p.shape,
    runSet: p.runs.map((r) => ({ ...r, items: r.items.map((i) => ({ ...i })) })),
    locks: (p.locks ?? []).map((l) => ({ ...l })),
    finishes: Object.fromEntries(FINISHES.map((f) => [f.id, p[f.id]]).concat([['kickColourId', p.kickColourId]])),
  };
}
function applySnapshot(s) {
  plan.value.walls = s.walls;
  plan.value.shape = s.shape;
  plan.value.runs = s.runSet;
  if (s.locks) plan.value.locks = s.locks;
  if (s.finishes) Object.assign(plan.value, s.finishes);
  kitchen.value.setPlan(plan.value);
  close();
  nudge.value++;
}

function undo() {
  if (readOnly()) return;
  const last = history.value.pop();
  if (!last) return;
  future.value.push(snapshotRoom());
  // A resize changed every run and the room itself, so it comes back as one step rather than
  // as four. Undoing a room a run at a time would leave the walls and the cabinets disagreeing.
  if (last.walls) { applySnapshot(last); return; }
  const run = plan.value.runs.find((r) => r.id === last.runId);
  if (!run) return;
  run.items = last.items;
  kitchen.value.buildRuns();
  close();
  nudge.value++;
}
function redo() {
  if (readOnly()) return;
  const next = future.value.pop();
  if (!next) return;
  history.value.push(snapshotRoom());
  applySnapshot(next);
}

/**
 * A different kitchen altogether: one opened from My kitchens, a shared link, or a fresh
 * start. One Undo step, so the wrong one costs nothing.
 */
function takePlan(p, { remember: keep = true } = {}) {
  if (keep) rememberRoom();
  plan.value = p;
  kitchen.value.setPlan(plan.value);
  close();
  designed.value = { options: [], rejected: [] };
  nudge.value++;
}
function openKitchen(p, name) {
  kitchens.value = false;
  // Opened from My kitchens: theirs, already made. Straight to ordinary editing.
  flow.value = null;
  takePlan(p);
  say(`Opened “${name}”. Undo brings the other one back.`);
}

/*
 * A new design for a room scanned some other day, from the Kitchens sheet.
 *
 * There are no service ticks to read here -- that question belongs to the card that comes up
 * with the scan -- so every service the scan found is locked, which is the same default the
 * ticks start at and the safe way round: a sink that stays put costs nothing, and a sink
 * that quietly moves costs a plumber.
 */
function designFromSaved(o) {
  kitchens.value = false;
  if (!o) return;
  takePlan(designFrom(o, maker, {
    keepLayout: false,
    lockServices: (o.services ?? []).map((sv) => ({ id: sv.id, role: sv.role })),
    keepIsland: true,
  }));
  showDesign();
  designForMe();
  // A new kitchen for a room scanned some other day is the same journey as one scanned just
  // now, minus the scan: the steps pick up at Choose and see it through to Review.
  flow.value = 'design';
  chosenOption.value = null;
  say(`A new kitchen for ${o.name}. Everything plumbed or gassed in stays where it is.`);
}

/**
 * Show a scanned room from the Kitchens sheet, read only, whatever is being worked on.
 *
 * It need not be this design's own room -- a person may just want to look at the kitchen
 * they scanned last month. `showDesign` puts that straight again when they come back.
 */
function lookAtOriginal(o) {
  kitchens.value = false;
  if (!o) return;
  original.value = o;
  showOriginal();
}
function freshKitchen() {
  kitchens.value = false;
  // Starting over IS the start of the journey: the hero comes back, and the wall fields go
  // back behind it.
  flow.value = 'start';
  typedWalls.value = false;
  chosenOption.value = null;
  const p = starterPlan(maker);
  for (const f of FINISHES) p[f.id] = plan.value[f.id];
  takePlan(p);
  const d = designOrFill(plan.value, maker, { seed: 1, count: 1 });
  if (d.options?.[0]) {
    plan.value.runs = d.options[0].plan.runs.map((r) => ({ ...r, items: r.items.map((i) => ({ ...i })) }));
    kitchen.value.setPlan(plan.value);
    nudge.value++;
  }
  mode.value = 'room';
  openSheet();
}

/**
 * The shapes most kitchens are. The room's outline is a box for all of them; what differs
 * is which walls carry cabinets. Anything else is a wall dragged on the plan.
 */
const SHAPES = [
  { id: 'single', name: 'One wall',
    glyph: '<svg width="30" height="24" viewBox="0 0 30 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7h20"/></svg>' },
  { id: 'galley', name: 'Galley',
    glyph: '<svg width="30" height="24" viewBox="0 0 30 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7h20"/><path d="M5 17h20"/></svg>' },
  { id: 'l', name: 'L',
    glyph: '<svg width="30" height="24" viewBox="0 0 30 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 5v12h19"/></svg>' },
  { id: 'u', name: 'U',
    glyph: '<svg width="30" height="24" viewBox="0 0 30 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 5v12h18V5"/></svg>' },
];

const WALL_NAMES = { back: 'Back wall', left: 'Left wall', right: 'Right wall', front: 'Facing wall' };

/** One field per wall that carries a run; the others are the room's outline and follow. */
const wallFields = computed(() => {
  nudge.value;
  const carrying = new Set(plan.value.runs.map((r) => r.wallId));
  return plan.value.walls
    .filter((w) => carrying.has(w.id))
    .map((w, i) => ({
      id: w.id, tag: String.fromCharCode(65 + i),
      name: WALL_NAMES[w.id] ?? w.id,
      mm: Math.round(room.length(w)),
    }));
});

function rememberRoom() {
  // The whole run SET, not just each run's contents: a shape change adds and removes runs,
  // and an undo that put the cabinets back but left the galley's front run standing was
  // the bug this replaced.
  future.value = [];
  history.value.push(snapshotRoom());
}

/**
 * A different shape.
 *
 * The outline stays -- the numbers somebody typed are still true of their room -- and the
 * runs move to the walls the shape says. Runs that survive keep their cabinets.
 */
function setShape(id) {
  if (readOnly()) return;
  /*
   * A PRESET REPLACES THE ROOM, and if the room was scanned that is a real loss.
   *
   * The walls, the doorways and anything the scan found go with it -- and the shape chips
   * sit right under the scan button, so it is an easy tap to make by accident. `rememberRoom`
   * means Undo brings all of it back; nobody knew that, because nothing said so.
   */
  const wasScanned = !!(plan.value.openings?.length || plan.value.islands?.length)
    || plan.value.walls.length > 4;
  rememberRoom();
  const bb = room.bounds(plan.value.walls);
  const walls = room.preset(id, { width: bb.maxX - bb.minX, depth: bb.maxY - bb.minY });
  const wanted = room.presetRuns(id);
  const keep = plan.value.runs.filter((r) => wanted.some(([wid, k]) => wid === r.wallId && k === r.kind));
  const added = wanted
    .filter(([wid, k]) => !keep.some((r) => r.wallId === wid && r.kind === k))
    .map(([wallId, kind]) => ({ id: `r-${wallId}-${kind}-${Date.now()}`, wallId, kind, items: [] }));
  plan.value.shape = id;
  plan.value.runs = [...keep, ...added];
  kitchen.value.setWalls(walls);
  close();
  if (wasScanned) say('Replaced the room you scanned with a preset shape. Undo brings it back.');
  nudge.value++;
}

/**
 * A wall, given a new length.
 *
 * Applied on `change` rather than on `input`: rebuilding the whole kitchen on every keystroke
 * means typing "4300" re-cuts the run at 4, then 43, then 430 -- three arrangements nobody
 * asked for and a visible stutter on the one screen that is meant to feel solid.
 */
function setWall(wallId, mm) {
  if (readOnly()) return;
  const w = plan.value.walls.find((x) => x.id === wallId);
  if (!w) return;
  const was = Math.round(room.length(w));
  const next = clampRoom(mm, was);
  if (next === was) return;
  rememberRoom();
  kitchen.value.setWallLength(wallId, next);
  close();
  nudge.value++;
  const stuck = plan.value.runs.find((r) => r.problem);
  if (stuck) say(stuck.problem);
}

/** An empty box or a typo is the old number, not zero. */
function clampRoom(mm, fallback) {
  if (!Number.isFinite(mm)) return fallback;
  return Math.min(ROOM.max, Math.max(ROOM.min, Math.round(mm)));
}

function swap(specId) {
  if (readOnly()) return;
  const run = selectedRun.value;
  const spec = maker.cabinets.find((c) => c.id === specId);
  if (!run || !spec) return;
  remember(run);
  const item = run.items.find((i) => i.id === selected.value.id);
  // Keep the width it already had, clamped into the new cabinet's range, so swapping a
  // cupboard for drawers does not shove every other cabinet along the wall.
  item.specId = spec.id;
  item.w = Math.min(Math.max(item.w, spec.stretch[0]), spec.stretch[1]);
  const drift = run.items.reduce((n, i) => n + i.w, 0) - (run.to - run.from);
  if (Math.abs(drift) > 0.5) settle(run, drift);
  kitchen.value.buildRuns();
  selected.value = run.items.find((i) => i.id === item.id);
  nudge.value++;
}

/** Give a width change back to, or take it from, the neighbours. */
function settle(run, drift) {
  for (const other of run.items) {
    if (other.id === selected.value?.id) continue;
    const spec = maker.cabinets.find((c) => c.id === other.specId);
    const lo = other.filler ? maker.limits.minFiller : (spec?.stretch[0] ?? other.w);
    const hi = other.filler ? 10000 : (spec?.stretch[1] ?? other.w);
    const room = drift > 0 ? other.w - lo : hi - other.w;
    if (room <= 0) continue;
    const move = Math.min(room, Math.abs(drift));
    other.w += drift > 0 ? -move : move;
    drift += drift > 0 ? -move : move;
    if (Math.abs(drift) <= 0.5) return;
  }
  if (Math.abs(drift) > 0.5) say('That will not fit without changing something else.');
}

function setWidth(mm) {
  if (readOnly()) return;
  const run = selectedRun.value;
  const item = run?.items.find((i) => i.id === selected.value.id);
  if (!item) return;
  const drift = mm - item.w;
  item.w = mm;
  settle(run, drift);
  kitchen.value.buildRuns();
  nudge.value++;
}

function add(specId) {
  if (readOnly()) return;
  const spec = maker.cabinets.find((c) => c.id === specId);
  // Tall units stand on the floor with the base run; overheads go on the overhead run.
  const wantKind = spec?.kind === 'wall' ? 'wall' : 'base';
  const run = plan.value.runs.find((r) => r.kind === wantKind);
  if (!run) { say(`There is no ${wantKind === 'wall' ? 'overhead' : 'base'} run to put it on.`); return; }
  remember(run);
  const r = kitchen.value.add(run.id, specId);
  if (!r.ok) { history.value.pop(); say(r.why); return; }
  nudge.value++;
}

function remove() {
  if (readOnly()) return;
  const run = selectedRun.value;
  remember(run);
  const r = kitchen.value.remove(run.id, selected.value.id);
  if (!r.ok) { history.value.pop(); say(r.why ?? 'That one has to stay.'); return; }
  close();
  nudge.value++;
}

function pick(field, id) {
  if (readOnly()) return;
  if (plan.value[field] === id) return;
  // A finish is one Undo step too; it was the one change that was not.
  rememberRoom();
  plan.value[field] = id;
  kitchen.value.setPlan(plan.value);
  nudge.value++;
}

// ── the little pictures ────────────────────────────────────────────────────────────
//
// Drawn rather than photographed: a maker's range changes and a cabinet icon that has to be
// re-shot every time it does is a reason not to add cabinets. These are four lines of SVG
// that read correctly at 40px, which is all a chooser needs.

/*
 * A cabinet, drawn small.
 *
 * This used to draw the same flat, wide rectangle for everything -- a 900 overhead and a
 * 2300 pantry to the ceiling came out as identical boxes, and the only thing telling a
 * fridge space from a broom cupboard apart was the word underneath. Tapping "Change it to"
 * on a tall unit showed a page of a dozen indistinguishable rounded rectangles.
 *
 * Two things fix that. The silhouette is now the cabinet's own w:h ratio, fitted into the
 * glyph's box rather than stamped at a fixed shape -- so a pantry actually reads as tall and
 * an overhead actually reads as wide, which is most of what makes a person recognise one at
 * a glance. And every role that has no doors or drawers of its own to draw -- a fridge
 * space, an oven tower, a rangehood, an open shelf -- gets a mark for what it is instead of
 * standing in as a blank box.
 */
function glyph(c) {
  const W = 46, H = 34, PAD = 4;
  const availW = W - PAD * 2, availH = H - PAD * 2;
  // The real proportion, clamped so neither a 900-wide overhead nor a 2300-tall pantry
  // collapses to a sliver the eye can't read as a cabinet at all.
  const ratio = Math.min(2.6, Math.max(0.24, (c.w || 600) / (c.h || 720)));
  let rw = availW, rh = availW / ratio;
  if (rh > availH) { rh = availH; rw = availH * ratio; }
  const x0 = (W - rw) / 2, y0 = (H - rh) / 2, x1 = x0 + rw, y1 = y0 + rh;

  const box = `<rect x="${x0.toFixed(1)}" y="${y0.toFixed(1)}" width="${rw.toFixed(1)}" height="${rh.toFixed(1)}" rx="2.5"
                fill="none" stroke="currentColor" stroke-width="1.6"/>`;
  let inner = '';

  if (c.drawers > 0) {
    for (let i = 1; i < c.drawers; i++) {
      const y = y0 + rh * (i / c.drawers);
      inner += `<line x1="${x0}" y1="${y.toFixed(1)}" x2="${x1}" y2="${y.toFixed(1)}" stroke="currentColor" stroke-width="1.1"/>`;
    }
  } else if (c.doors > 1) {
    // One divider per join, not one line at the midpoint -- a 3-door pantry front is two
    // lines, not the single centre line a 2-door front gets.
    for (let i = 1; i < c.doors; i++) {
      const x = x0 + rw * (i / c.doors);
      inner += `<line x1="${x.toFixed(1)}" y1="${y0}" x2="${x.toFixed(1)}" y2="${y1}" stroke="currentColor" stroke-width="1.1"/>`;
    }
  }

  const midX = (x0 + x1) / 2, midY = (y0 + y1) / 2;
  if (c.role === 'sink') {
    inner += `<ellipse cx="${midX.toFixed(1)}" cy="${(y0 + rh * 0.32).toFixed(1)}" rx="${Math.min(11, rw * 0.34).toFixed(1)}" ry="${Math.min(5, rh * 0.16).toFixed(1)}" fill="currentColor" opacity=".45"/>`;
  } else if (c.role === 'cooktop') {
    const dx = Math.min(9, rw * 0.26), dy = Math.min(5, rh * 0.22);
    inner += [[-dx, -dy], [dx, -dy], [-dx, dy], [dx, dy]]
      .map(([ox, oy]) => `<circle cx="${(midX + ox).toFixed(1)}" cy="${(midY + oy).toFixed(1)}" r="2.6" fill="currentColor" opacity=".45"/>`).join('');
  } else if (c.role === 'fridge') {
    // The two French-door panels, and a handle stub on each -- the same silhouette the 3D
    // model draws, at glyph scale.
    inner += `<line x1="${midX.toFixed(1)}" y1="${y0}" x2="${midX.toFixed(1)}" y2="${y1}" stroke="currentColor" stroke-width="1.1"/>`;
    inner += [-1, 1].map((s) => `<line x1="${(midX + s * 2.2).toFixed(1)}" y1="${(y0 + rh * 0.22).toFixed(1)}" x2="${(midX + s * 2.2).toFixed(1)}" y2="${(y0 + rh * 0.5).toFixed(1)}" stroke="currentColor" stroke-width="1.4" opacity=".7"/>`).join('');
  } else if (c.role === 'oven') {
    // The aperture at roughly waist height, with the bar across the top of the glass.
    const ow = rw * 0.62, oh = rh * 0.3, ox = midX - ow / 2, oy = y0 + rh * 0.42;
    inner += `<rect x="${ox.toFixed(1)}" y="${oy.toFixed(1)}" width="${ow.toFixed(1)}" height="${oh.toFixed(1)}" rx="1" fill="none" stroke="currentColor" stroke-width="1.1" opacity=".75"/>`;
    inner += `<line x1="${(ox + 1.5).toFixed(1)}" y1="${(oy + oh - 2).toFixed(1)}" x2="${(ox + ow - 1.5).toFixed(1)}" y2="${(oy + oh - 2).toFixed(1)}" stroke="currentColor" stroke-width="1.3" opacity=".75"/>`;
  } else if (c.role === 'dishwasher') {
    // A control strip along the top edge and a handle under it -- a dishwasher space has
    // no doors of its own to draw, so without this it was the one blank box in the grid.
    inner += `<line x1="${(x0 + 3).toFixed(1)}" y1="${(y0 + rh * 0.22).toFixed(1)}" x2="${(x1 - 3).toFixed(1)}" y2="${(y0 + rh * 0.22).toFixed(1)}" stroke="currentColor" stroke-width="1.1" opacity=".7"/>`;
    inner += `<line x1="${(midX - rw * 0.22).toFixed(1)}" y1="${(y0 + rh * 0.38).toFixed(1)}" x2="${(midX + rw * 0.22).toFixed(1)}" y2="${(y0 + rh * 0.38).toFixed(1)}" stroke="currentColor" stroke-width="1.4" opacity=".55"/>`;
  } else if (c.role === 'rangehood') {
    // The chimney, the one silhouette on the whole grid that is not a rectangle.
    const cw = rw * 0.5;
    inner += `<path d="M${(midX - cw / 2).toFixed(1)},${(y0 + rh * 0.42).toFixed(1)} L${midX.toFixed(1)},${y0.toFixed(1)} L${(midX + cw / 2).toFixed(1)},${(y0 + rh * 0.42).toFixed(1)}"
               fill="none" stroke="currentColor" stroke-width="1.2"/>`;
  } else if (c.role === 'open') {
    // Shelves, not a front: dashed, because a dash is what says "nothing closes this".
    for (const f of [0.36, 0.66]) {
      inner += `<line x1="${x0}" y1="${(y0 + rh * f).toFixed(1)}" x2="${x1}" y2="${(y0 + rh * f).toFixed(1)}" stroke="currentColor" stroke-width="1.1" stroke-dasharray="2.5 2"/>`;
    }
  } else if (c.role === 'corner') {
    // A small notch at the return corner -- the one detail that says this unit turns.
    const s = Math.min(7, rw * 0.3, rh * 0.3);
    inner += `<path d="M${(x1 - s).toFixed(1)},${y0} L${x1},${y0} L${x1},${(y0 + s).toFixed(1)}"
               fill="none" stroke="currentColor" stroke-width="1.3" opacity=".8"/>`;
  }

  return `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" aria-hidden="true">${box}${inner}</svg>`;
}

/** The door profile, drawn: a slab, a framed centre, or grooves. */
/*
 * The door face and the handle, drawn ON the swatch rather than beside it.
 *
 * These used to be SVGs at a fixed pixel size, centred in the chip and then shrunk with a
 * CSS transform in the one place the chip was smaller. A fixed size in a box whose size is
 * decided by CSS is a guess, and the guess was wrong: the door's grooves ran out over the
 * right-hand edge of a 34px chip and the handle sat off its own square. They scale to
 * whatever chip they are put in now, and there is one place -- the viewBox -- that decides
 * how much of it they fill.
 *
 * A door face is a rectangle, so it stretches to the chip (`preserveAspectRatio="none"`) and
 * its detail stays where it belongs. A handle is an object, so it keeps its shape.
 */
function styleGlyph(d) {
  const w = 54, h = 40;
  const svg = (inner, fit) => `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="${fit}"`
    + ` width="100%" height="100%" aria-hidden="true">${inner}</svg>`;
  if (d.style === 'shaker') {
    return svg(`<rect x="7" y="5" width="${w - 14}" height="${h - 10}" rx="2" fill="none"
            stroke="rgba(0,0,0,.34)" stroke-width="1.4"/>`, 'none');
  }
  if (d.style === 'vgroove') {
    const lines = [0.28, 0.44, 0.6, 0.76].map((f) =>
      `<line x1="${w * f}" y1="4" x2="${w * f}" y2="${h - 4}"
             stroke="rgba(0,0,0,.28)" stroke-width="1.3"/>`).join('');
    return svg(lines, 'none');
  }
  return svg('', 'none');
}

function handleGlyph(hd) {
  const svg = (inner) => '<svg viewBox="0 0 40 24" preserveAspectRatio="xMidYMid meet"'
    + ` width="100%" height="100%" aria-hidden="true">${inner}</svg>`;
  if (hd.style === 'none') return svg('');
  return svg(hd.style === 'bar'
    ? `<rect x="8" y="10" width="24" height="4" rx="2" fill="${hd.colour}"/>
       <rect x="9" y="14" width="3" height="4" fill="${hd.colour}"/>
       <rect x="28" y="14" width="3" height="4" fill="${hd.colour}"/>`
    : `<path d="M10 16 V11 H30 V16" fill="none" stroke="${hd.colour}" stroke-width="4" stroke-linecap="round"/>`);
}

/** A gloss door catches the light; a matt one does not. The chip should say which. */
function finishChip(d) {
  return d.gloss > 0.4
    ? { background: `linear-gradient(150deg, ${tint(d.colour, 34)} 0%, ${d.colour} 42%, ${tint(d.colour, -12)} 100%)` }
    : { background: d.colour };
}
/**
 * The fittings, drawn small.
 *
 * Each one shows the thing that actually differs -- a chimney or a slim hood, a door front
 * or a control strip, one oven or two -- because the names alone ("semi-integrated") mean
 * nothing to somebody who has not bought a dishwasher lately.
 */
function fittingGlyph(f, o) {
  const svg = (inner) => '<svg viewBox="0 0 40 30" preserveAspectRatio="xMidYMid meet"'
    + ` width="100%" height="100%" aria-hidden="true">${inner}</svg>`;
  const line = 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"';
  if (f.id === 'rangehoodId') {
    if (o.style === 'canopy') return svg(`<path d="M6 16 L12 9 H28 L34 16 Z" ${line}/>`
      + `<path d="M17 9 V3 H23 V9" ${line}/>`);
    if (o.style === 'under') return svg(`<rect x="7" y="4" width="26" height="14" rx="1" ${line}/>`
      + `<rect x="7" y="19" width="26" height="6" rx="1" ${line}/>`);
    return svg(`<rect x="7" y="4" width="26" height="19" rx="1" ${line}/>`
      + `<path d="M9 25 H31" ${line}/>`);
  }
  if (f.id === 'fridgeId') {
    if (o.style === 'integrated') return svg(`<rect x="9" y="3" width="22" height="24" rx="1" ${line}/>`
      + `<path d="M20 3 V27" ${line}/>`);
    return svg(`<rect x="9" y="3" width="22" height="24" rx="1" ${line}/>`
      + `<path d="M9 12 H31" ${line}/>`
      + `<path d="M13 7 V9" ${line}/><path d="M13 16 V19" ${line}/>`);
  }
  if (f.id === 'dishwasherId') {
    if (o.style === 'integrated') return svg(`<rect x="9" y="4" width="22" height="22" rx="1" ${line}/>`
      + `<path d="M13 8 H27" ${line}/>`);
    if (o.style === 'semi') return svg(`<rect x="9" y="4" width="22" height="6" rx="1" ${line}/>`
      + `<rect x="9" y="12" width="22" height="14" rx="1" ${line}/>`);
    return svg(`<rect x="9" y="4" width="22" height="22" rx="1" ${line}/>`
      + `<circle cx="14" cy="8" r="1.4" fill="currentColor"/>`
      + `<circle cx="19" cy="8" r="1.4" fill="currentColor"/>`);
  }
  // The glyph IS the stack: one box per aperture, at the aperture's own proportion, so a
  // compact oven reads as the shorter box it is.
  const stack = o.stack ?? [595];
  const total = stack.reduce((a, b) => a + b, 0);
  let y = 15 - Math.min(12, total / 90);
  const boxes = stack.map((h) => {
    const hh = Math.max(5, (h / total) * Math.min(24, total / 45));
    const r = `<rect x="9" y="${y.toFixed(1)}" width="22" height="${hh.toFixed(1)}" rx="1" ${line}/>`
      + `<path d="M12 ${(y + 3).toFixed(1)} H28" ${line}/>`;
    y += hh + 1.5;
    return r;
  }).join('');
  return svg(boxes);
}

/** The floor, as a swatch: boards get board lines, tile gets a grid, concrete gets mottle. */
function floorChip(fl) {
  if (fl.kind === 'boards') {
    return { background: `repeating-linear-gradient(0deg, ${fl.colour} 0 9px,
             ${fl.grain ?? tint(fl.colour, -20)} 9px 10px)` };
  }
  if (fl.kind === 'tile') {
    const j = fl.grout ?? tint(fl.colour, -18);
    return { background: `repeating-linear-gradient(0deg, ${j} 0 2px, transparent 2px 20px),
             repeating-linear-gradient(90deg, ${j} 0 2px, transparent 2px 20px), ${fl.colour}` };
  }
  return { background: `radial-gradient(circle at 26% 32%, ${tint(fl.colour, 16)} 0 24%, transparent 26%),
           radial-gradient(circle at 72% 68%, ${tint(fl.colour, -14)} 0 22%, transparent 24%), ${fl.colour}` };
}
function stoneChip(b) {
  return b.fleck
    ? { background: `radial-gradient(circle at 30% 26%, ${b.fleck} 0 1.2px, transparent 1.4px),
                     radial-gradient(circle at 68% 60%, ${b.fleck} 0 1.5px, transparent 1.7px),
                     radial-gradient(circle at 44% 78%, ${b.fleck} 0 1px, transparent 1.2px),
                     ${b.colour}` }
    : { background: b.colour };
}
function tint(hex, amt) {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map((v) => Math.max(0, Math.min(255, v + amt)));
  return `rgb(${c.join(',')})`;
}
</script>

<style scoped>
.planner { --background: var(--k-room); }

.stage {
  position: absolute; inset: 0; width: 100%; height: 100%;
  display: block; touch-action: none;   /* the gestures are ours, not the browser's */
}

.brandbar {
  position: absolute; top: 0; left: 0; right: 0;
  display: flex; align-items: center; gap: 12px;
  padding: calc(10px + env(safe-area-inset-top)) 16px 10px;
  font: 600 15px/1 var(--ion-font-family, system-ui);
  color: var(--k-ink);
  /* Fades into the room rather than sitting on a bar: the kitchen runs under it. */
  background: linear-gradient(var(--k-room) 55%, transparent);
  pointer-events: none;
  z-index: 2;
}
.mark { letter-spacing: -.01em; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ghosts { display: flex; gap: 0; pointer-events: auto; }
.ghosts .ghost { margin-left: 0; padding: 8px 4px; min-width: 32px; font-size: 17px; }
.view {
  pointer-events: auto; display: flex; gap: 2px; padding: 3px; border-radius: 10px;
  background: color-mix(in oklab, var(--k-paper) 86%, transparent);
  box-shadow: 0 1px 3px rgba(0,0,0,.10);
}
.view button {
  border: 0; cursor: pointer; padding: 7px 12px; border-radius: 8px; min-height: 34px;
  font: 500 12px/1 var(--ion-font-family, system-ui);
  background: transparent; color: var(--k-dim);
}
.view button.on { background: var(--k-ink); color: var(--k-paper); }

/* Now / New. Its own row under the brandbar rather than beside 3D/Plan: two segmented
   controls and three ghost buttons do not fit across a phone, and the row also has to
   hold the sentence that says what Now means. Left of the rail, which is on the right. */
.asscanned {
  position: absolute; left: 16px; right: 76px; z-index: 3;
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  pointer-events: none;
  /* Same reason as the rail: an overlay over the canvas that leaves touch-action alone
     hands the gesture to WebKit's own recogniser. */
  touch-action: none;
}
.asscanned .note {
  margin: 0; flex: 1 1 150px; min-width: 0;
  padding: 6px 9px; border-radius: 8px;
  background: color-mix(in oklab, var(--k-paper) 86%, transparent);
  font: 500 11.5px/1.35 var(--ion-font-family, system-ui); color: var(--k-dim);
}
.asscanned .file {
  pointer-events: auto; border: 0; cursor: pointer; padding: 7px 11px; border-radius: 8px;
  font: 600 11.5px/1 var(--ion-font-family, system-ui);
  background: color-mix(in oklab, var(--k-paper) 86%, transparent); color: var(--k-accent);
  box-shadow: 0 1px 3px rgba(0,0,0,.10);
}
.ghost {
  margin-left: auto; pointer-events: auto;
  display: inline-flex; align-items: center; gap: 6px;
  border: 0; background: none; color: var(--k-dim);
  font: 500 13px/1 inherit; padding: 8px; min-height: 40px; cursor: pointer;
}
.ghost:disabled { opacity: .35; }

/* The rail: two round-cornered buttons in the model's top right, out of the way of the
   kitchen itself and above everything but the sheet. */
.rail {
  z-index: 3; position: absolute; right: 12px;
  display: flex; flex-direction: column; gap: 7px;
  /* The canvas owns two-finger gestures (touch.js), but only where the canvas itself is
     hit. This sits ON TOP of the canvas at exactly the corner two-finger orbit/pan/zoom
     starts from, and without this a touch that lands on or near a button here is left at
     `touch-action: auto` -- so WebKit's own gesture recognizer claims it instead of ours,
     and draws its own transient UI over the page: a hairline down the screen from wherever
     the second finger landed. Every overlay sitting over the 3D view needs the same rule;
     the bottom sheet and its buttons do not, because normal scrolling there is wanted. */
  touch-action: none;
}
.cabinet .who .acts { display: flex; align-items: center; gap: 4px; }
.cabinet .who .del {
  width: 44px; height: 44px; display: grid; place-items: center; cursor: pointer;
  border: 0; border-radius: 11px; background: none; color: var(--k-warn);
}
.cabinet .who .del:active { background: color-mix(in oklab, var(--k-warn) 10%, transparent); }

.rail button.icon {
  min-width: 0; width: 46px; height: 46px; padding: 0; gap: 0;
  align-self: flex-end;
}
.rail button {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  width: 58px; min-height: 54px; padding: 7px 4px; cursor: pointer;
  border: 0; border-radius: 13px;
  background: color-mix(in oklab, var(--k-paper) 90%, transparent); color: var(--k-dim);
  font: 500 10px/1 var(--ion-font-family, system-ui);
  box-shadow: 0 2px 8px rgba(0,0,0,.12);
}
.rail button:active { background: var(--k-paper); }
.rail button:disabled { opacity: .5; }

.hint {
  z-index: 3;
  position: absolute; left: 50%; top: calc(58px + env(safe-area-inset-top));
  transform: translate(-50%, -6px);
  max-width: calc(100% - 40px);
  touch-action: none;
  padding: 8px 15px; border-radius: 999px;
  background: var(--k-ink); color: var(--k-paper);
  font: 500 12.5px/1.35 var(--ion-font-family, system-ui);
  opacity: 0; transition: opacity .16s ease, transform .16s ease;
  pointer-events: none; text-align: center;
}
.hint.on { opacity: 1; transform: translate(-50%, 0); }
.hint.warn { background: var(--k-warn); }

.sheet {
  z-index: 3;
  position: absolute; left: 0; right: 0; bottom: var(--bar-h);
  background: var(--k-paper);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -1px 0 var(--k-line), 0 -14px 36px rgba(0,0,0,.12);
  padding: 0 0 14px;
  max-height: 48vh; overflow-y: auto;
  transition: transform .26s cubic-bezier(.2, .8, .2, 1);
  will-change: transform;
}
/* While a finger is on it there is no transition, or the sheet lags behind the thumb. */
.sheet.sliding { transition: none; }

/*
 * THE STEPS THAT NEED MORE THAN HALF THE SCREEN.
 *
 * 48vh is right for every panel in this app except the two the guided flow puts up. The
 * card after a scan holds a sentence about the room, a checklist of what is plumbed in, the
 * island, and the button that acts on all of it; the Start card on a device that cannot scan
 * holds the shape, both wall fields and Design it for me. Either is about 420px on a 390x844
 * phone, which put the one button each screen exists for just under the fold. A person who
 * has to scroll to find the only button on a screen has already been told the app is
 * confusing, which is exactly what the owner said about this one.
 *
 * A MAXIMUM, not a height: the sheet is still as tall as its content, so the hero card on a
 * phone that CAN scan is still four lines tall and the kitchen still fills the screen.
 *
 * Still draggable, still leaves the kitchen showing above it, and the camera reframes on its
 * own because `covered` is measured rather than assumed.
 */
.sheet.guided { max-height: 60vh; }

/* The handle is a HANDLE: 30px of it, the height of a thumb, and the only thing left on
   screen when the sheet is down -- so there is always something to pull it back by. */
.grab {
  position: sticky; top: 0; z-index: 1;
  display: grid; place-items: center;
  height: 30px; cursor: grab; touch-action: none;
  background: var(--k-paper); border-radius: 20px 20px 0 0;
}
.grab:active { cursor: grabbing; }
.grab span {
  display: block; width: 38px; height: 4px;
  border-radius: 999px; background: var(--k-line);
}
.grab:focus-visible { outline: 2px solid var(--k-ink); outline-offset: -3px; }

/* ── where you are ─────────────────────────────────────────────────────────────
   One line under the handle, above whatever the sheet is showing. Sticky with the
   handle, because "how far through am I" is a question people ask while scrolling.

   Four numbered words on a 390px phone is 22px of vertical space and there is no
   version of this that earns more. The steps behind you are tappable and the ones
   ahead are not: a progress line you can skip forward on is a menu, and the whole
   complaint this answers was that there were already too many menus. */
.steps {
  position: sticky; top: 30px; z-index: 1;
  display: flex; align-items: center; gap: 2px; flex-wrap: nowrap;
  margin: 0; padding: 0 12px 8px; list-style: none;
  background: var(--k-paper);
}
.steps li { flex: 0 1 auto; min-width: 0; }
.steps li + li::before {
  content: '·'; color: var(--k-line); padding-right: 2px;
}
.steps button {
  display: inline-flex; align-items: center; gap: 5px; max-width: 100%;
  border: 0; background: none; padding: 4px 4px; min-height: 26px;
  font: 500 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-mute);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  cursor: pointer;
}
.steps button:disabled { cursor: default; }
.steps i {
  display: grid; place-items: center; flex: 0 0 auto;
  width: 16px; height: 16px; border-radius: 999px;
  background: var(--k-fill); color: var(--k-mute);
  font: 600 9.5px/1 var(--ion-font-family, system-ui); font-style: normal;
}
.steps .done button { color: var(--k-dim); }
.steps .done i { background: color-mix(in oklab, var(--k-accent) 22%, transparent); color: var(--k-accent); }
.steps .on button { color: var(--k-ink); font-weight: 700; }
.steps .on i { background: var(--k-ink); color: var(--k-paper); }

/* The way on, at the foot of a step. Same weight as the primary in ScanFlow, because it
   is the same kind of promise: press this and you are one step further along. */
.next {
  margin: 14px 0 2px; width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 0; border-radius: 11px; cursor: pointer; padding: 14px; min-height: 50px;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 14.5px/1 var(--ion-font-family, system-ui);
}
.next:active { opacity: .9; }
/* Inside the finish list, which is one bordered card of hairline-separated rows: the
   button becomes the last row rather than a button sitting on top of one. */
.finish .next { margin: 0; border-radius: 0; }

/* ── the bar ──────────────────────────────────────────────────────────────────── */
.planner { --bar-h: calc(84px + env(safe-area-inset-bottom)); }
.bar {
  z-index: 4;
  position: absolute; left: 0; right: 0; bottom: 0;
  height: var(--bar-h);
  display: flex; align-items: stretch; gap: 4px;
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
  background: var(--k-paper); box-shadow: 0 -1px 0 var(--k-line);
}
.bar button {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 5px; min-height: 54px; padding: 0 4px; border: 0; border-radius: 12px; cursor: pointer;
  background: transparent; color: var(--k-mute); position: relative;
  font: 500 12px/1 var(--ion-font-family, system-ui);
}
/* One black button: the tab you are on. The way out lives at the top of Review. */
.bar button.on { background: var(--k-ink); color: var(--k-paper); }
/* Something to fix before it can go: a dot on Review, the way a badge says "unread". */
.bar button.todo::after {
  content: ''; position: absolute; top: 8px; right: 22%; width: 7px; height: 7px;
  border-radius: 999px; background: var(--k-warn);
}
.bar button.on.todo::after { background: var(--k-paper); }

/*
 * They WRAP. They used to be a sideways-scrolling row, which was fine at four colours and
 * is not fine at seventeen: the row is inside a sheet that is itself draggable, so a
 * horizontal swipe on it fights the sheet's own gesture and most of the palette could not
 * be reached at all. A grid shows every option at once and has no gesture to lose.
 */
.swatches {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 10px; padding: 2px 14px 6px;
}
.swatch, .cab {
  flex: 0 0 auto; width: 92px; border: 0; background: none; cursor: pointer;
  padding: 0; text-align: left; color: var(--k-dim);
}
/* In the wrapping grid a swatch takes its cell, so the palette fills the width evenly
   instead of leaving a ragged gap at the end of every row. */
.swatches .swatch { width: auto; }
.chip {
  display: grid; place-items: center; overflow: hidden;
  width: 92px; height: 62px; border-radius: 10px;
  border: 1px solid var(--k-line);
}
.swatches .chip { width: auto; }
/* The artwork IS the swatch: it fills the chip and is clipped by its corners, so a door
   face and its colour are one thing rather than a drawing sitting on top of a square. */
.chip .glyph { display: block; width: 100%; height: 100%; }
/* A fitting is a line drawing, not a swatch: it needs air around it and the ink colour. */
.chip .glyph.fit { padding: 14%; box-sizing: border-box; color: var(--k-dim); }
/* v-html content carries no scope attribute, so the SVG itself is only reachable through
   :deep -- which is why this used to be a transform on the span instead. */
.chip .glyph :deep(svg) { display: block; width: 100%; height: 100%; }
.chip.pale { background: var(--k-fill); }
.cab .glyph {
  display: grid; place-items: center;
  width: 92px; height: 62px; border-radius: 10px;
  background: var(--k-fill); border: 1px solid var(--k-line); color: var(--k-dim);
}
.swatch.on .chip, .cab.on .glyph { outline: 2px solid var(--k-ink); outline-offset: 2px; }
/* w × h, in the units a maker cuts to. Not depth -- every base is 560, every tall unit's
   depth barely varies, and width and height are the two numbers that decide whether
   something fits where you are looking at putting it. */
.dims {
  display: block; margin-top: 1px;
  font: 400 10px/1 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.grid .dims { font-size: 9.5px; }
.name {
  display: block; margin-top: 7px;
  font: 400 12px/1.25 var(--ion-font-family, system-ui); color: var(--k-dim);
}

/* ── cabinets: the range, by kind ─────────────────────────────────────────────── */
.cabinets { padding: 0 16px 2px; }
.kinds { display: flex; gap: 6px; align-items: center; padding: 0 0 10px; }
.kinds button {
  border: 0; border-radius: 8px; cursor: pointer; padding: 8px 12px; min-height: 34px;
  font: 500 12px/1 var(--ion-font-family, system-ui);
  background: var(--k-fill); color: var(--k-dim);
}
.kinds button.on { background: var(--k-ink); color: var(--k-paper); }
.kinds .count { margin-left: auto; font: 400 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-mute); }
.grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 9px; }
.grid .cab { width: auto; }
.grid .cab .glyph { width: auto; height: 56px; }
.grid .cab .glyph svg { width: 42px; height: 31px; }
.grid /* w × h, in the units a maker cuts to. Not depth -- every base is 560, every tall unit's
   depth barely varies, and width and height are the two numbers that decide whether
   something fits where you are looking at putting it. */
.dims {
  display: block; margin-top: 1px;
  font: 400 10px/1 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.grid .dims { font-size: 9.5px; }
.name { font-size: 11px; margin-top: 6px; }
.cabinets .fine { margin: 12px 0 0; }

/* ── finish: five rows, every choice on show ──────────────────────────────────── */
.finish { margin: 0 16px; display: grid; gap: 1px; background: var(--k-line); border: 1px solid var(--k-line); border-radius: 12px; overflow: hidden; }
.finish .row { background: var(--k-paper); }
.finish .head {
  display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 13px;
  border: 0; background: none; cursor: pointer; color: var(--k-mute); text-align: left;
}
.finish .head .chip { width: 34px; height: 34px; flex: 0 0 auto; border-radius: 8px; }
.finish .text { flex: 1; }
.finish .k { display: block; font: 400 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-mute); }
.finish .v { display: block; margin-top: 4px; font: 500 13.5px/1 var(--ion-font-family, system-ui); color: var(--k-ink); }
.finish .roomnote {
  background: var(--k-paper); margin: 0; padding: 11px 13px;
  font: 400 11.5px/1.45 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.finish .head svg { transition: transform .16s ease; }
.finish .row.open .head svg { transform: rotate(180deg); }
.finish .row.open .head { background: var(--k-fill); }
.finish .swatches { padding: 10px 13px 12px; }

.askbtn {
  z-index: 5;
  position: absolute; right: 14px;
  display: inline-flex; align-items: center; gap: 7px;
  border: 0; border-radius: 999px; cursor: pointer; padding: 11px 15px 11px 13px; min-height: 44px;
  touch-action: none;
  background: var(--k-accent); color: var(--k-paper);
  font: 600 13px/1 var(--ion-font-family, system-ui);
  box-shadow: 0 6px 18px rgba(0,0,0,.18);
  transition: bottom .26s cubic-bezier(.2, .8, .2, 1);
}
.askbtn.on { background: var(--k-ink); }
.read { margin-top: 12px; padding: 12px; border-radius: 12px; background: var(--k-fill); }
.read .label { margin-top: 0; padding: 0; }
.read .fine { margin: 0 0 8px; }
.readrow { display: flex; align-items: center; gap: 10px; }
.readrow .design { margin-top: 10px; flex: 1; }
.readrow .quiet { margin: 10px 0 0; }
.design {
  margin: 14px 0 0; width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  border: 0; border-radius: 11px; cursor: pointer; padding: 14px; min-height: 50px;
  background: var(--k-ink); color: var(--k-paper);
  font: 600 14px/1 var(--ion-font-family, system-ui);
}
.lock {
  margin: 14px 16px 0; width: calc(100% - 32px);
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  border: 1px solid var(--k-line); border-radius: 11px; cursor: pointer;
  background: var(--k-paper); color: var(--k-dim);
  font: 500 14px/1 var(--ion-font-family, system-ui);
  padding: 13px; min-height: 48px;
}
.lock.on { background: var(--k-ink); color: var(--k-paper); border-color: var(--k-ink); }
.quiet {
  margin: 12px 0 2px; display: inline-flex; align-items: center; gap: 8px;
  border: 0; background: none; cursor: pointer; padding: 8px 2px; min-height: 40px;
  font: 500 12.5px/1 var(--ion-font-family, system-ui); color: var(--k-accent);
}

/* ── one cabinet ─────────────────────────────────────────────────────────────── */
.cabinet .who {
  display: flex; align-items: flex-start; gap: 12px; padding: 0 16px 4px;
}
.cabinet h2 {
  margin: 0; font: 600 17px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.cabinet .who p {
  margin: 3px 0 0; font: 400 13px/1.3 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.label {
  margin: 12px 0 7px; padding: 0 16px;
  font: 500 11.5px/1 var(--ion-font-family, system-ui);
  letter-spacing: .05em; text-transform: uppercase; color: var(--k-mute);
}

.width { display: flex; align-items: center; gap: 14px; padding: 0 16px; }
.width input {
  flex: 1; accent-color: var(--k-ink); height: 34px;
}
.mm {
  font: 600 15px/1 var(--ion-font-family, system-ui); color: var(--k-ink);
  min-width: 5.5ch; text-align: right; font-variant-numeric: tabular-nums;
}
.mm i { font: 400 11px/1 inherit; color: var(--k-mute); font-style: normal; margin-left: 2px; }

.danger {
  margin: 10px 16px 2px; width: calc(100% - 32px);
  border: 1px solid var(--k-line); border-radius: 11px; cursor: pointer;
  background: none; color: var(--k-warn);
  font: 500 14px/1 var(--ion-font-family, system-ui);
  padding: 14px; min-height: 48px;
}

.room { padding: 0 16px 4px; }

/* The scan, offered properly. A row the width of the panel, with the reason it exists
   written next to it -- the old version was a grey text link under the number fields, which
   is where you put something you hope nobody presses. */
.scan {
  display: flex; align-items: center; gap: 13px; width: 100%; text-align: left;
  padding: 12px 13px; margin: 0 0 4px; min-height: 68px;
  border: 1px solid var(--k-ink); border-radius: 13px; cursor: pointer;
  background: var(--k-ink); color: var(--k-paper);
}
.scan:active { opacity: .9; }
.scan:disabled { opacity: .6; }
.scan .glyph { flex: 0 0 auto; display: grid; place-items: center; opacity: .9; }
.scan .text { flex: 1; display: grid; gap: 3px; min-width: 0; }
.scan .k { font: 600 14.5px/1.2 var(--ion-font-family, system-ui); }
.scan .b { font: 400 11.5px/1.35 var(--ion-font-family, system-ui); opacity: .72; }
.scan .go { flex: 0 0 auto; opacity: .6; }

/* And when it cannot run: still there, still explained, out of the way. */
.noscan {
  display: flex; align-items: flex-start; gap: 9px; margin: 0 0 6px; padding: 9px 11px;
  border: 1px dashed var(--k-line); border-radius: 11px;
  font: 400 11.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.noscan svg { flex: 0 0 auto; margin-top: 1px; }
.noscan b { font-weight: 600; color: var(--k-dim); }
.shapes { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.shapes button {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; min-height: 60px; padding: 8px 4px; border-radius: 11px; cursor: pointer;
  border: 1px solid var(--k-line); background: var(--k-paper); color: var(--k-mute);
}
.shapes button.on { border-color: var(--k-ink); background: var(--k-ink); color: var(--k-paper); }
.shapes /* w × h, in the units a maker cuts to. Not depth -- every base is 560, every tall unit's
   depth barely varies, and width and height are the two numbers that decide whether
   something fits where you are looking at putting it. */
.dims {
  display: block; margin-top: 1px;
  font: 400 10px/1 var(--ion-font-family, system-ui); color: var(--k-mute);
}
.grid .dims { font-size: 9.5px; }
.name { margin: 0; font: 500 11px/1 var(--ion-font-family, system-ui); color: inherit; }
.walls { display: grid; gap: 7px; }
.walls label {
  display: flex; align-items: center; gap: 10px; padding: 8px 10px 8px 10px;
  border: 1px solid var(--k-line); border-radius: 11px; background: var(--k-paper);
  font: 400 13px/1.2 var(--ion-font-family, system-ui); color: var(--k-dim);
}
.walls .tag {
  width: 24px; height: 24px; flex: 0 0 auto; display: grid; place-items: center;
  border-radius: 7px; background: var(--k-fill);
  font: 600 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-dim);
}
.walls .which { flex: 1; }
.walls .field { width: 118px; }
.walls .field input { min-height: 40px; padding: 8px 10px; }
.field { display: flex; align-items: center; gap: 6px; }
.field input {
  flex: 1; width: 100%; min-width: 0;
  border: 1px solid var(--k-line); border-radius: 10px; background: var(--k-paper);
  padding: 12px 11px; min-height: 48px;
  font: 500 16px/1 var(--ion-font-family, system-ui); color: var(--k-ink);
  font-variant-numeric: tabular-nums;
}
.field input:focus { outline: 2px solid var(--k-ink); outline-offset: 1px; }
.field i { font: 400 12px/1 inherit; color: var(--k-mute); font-style: normal; }
.room .fine { margin: 11px 0 2px; }

.schedule { padding: 4px 16px 2px; }
.lede { margin: 0 0 4px; font: 400 14.5px/1.5 var(--ion-font-family, system-ui); color: var(--k-dim); }
.lede b { font-weight: 600; }
.fine { margin: 0 0 14px; font: 400 12.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute); }
.images { display: flex; gap: 8px; overflow-x: auto; margin: 2px 0 10px; scrollbar-width: none; }
.images::-webkit-scrollbar { display: none; }
.images figure { flex: 0 0 auto; width: 118px; margin: 0; }
.images img { display: block; width: 118px; height: 80px; object-fit: cover; border-radius: 9px; border: 1px solid var(--k-line); cursor: zoom-in; }
.images figcaption { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; font: 400 11px/1.2 var(--ion-font-family, system-ui); color: var(--k-mute); }
.images figcaption button { border: 0; background: none; color: var(--k-mute); font-size: 16px; line-height: 1; cursor: pointer; padding: 2px 6px; }
.addimg { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 7px; margin: 0 0 4px; }
.addimg button {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px;
  text-align: center; padding: 9px 6px; min-height: 64px;
  border: 1px solid var(--k-line); border-radius: 11px; background: var(--k-paper); cursor: pointer;
  color: var(--k-dim);
}
.addimg button:active { background: var(--k-fill); }
.addimg button:disabled { opacity: .5; }
.addimg .glyph { display: grid; place-items: center; color: var(--k-dim); }
.addimg .k { font: 500 11.5px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink); }
.picnote { margin: 8px 0 4px; }
.room .quiet { margin-top: 6px; }
.island-add {
  width: 100%; margin: 10px 0 2px; display: flex; align-items: center; justify-content: center;
  gap: 9px; border: 1px solid var(--k-line); border-radius: 12px; cursor: pointer;
  background: var(--k-paper); color: var(--k-dim);
  font: 500 13.5px/1 var(--ion-font-family, system-ui); padding: 14px; min-height: 50px;
}
.island-add:active { background: var(--k-fill); }

.isle {
  margin: 10px 0 2px; padding: 11px 12px 12px; border-radius: 12px;
  border: 1px solid var(--k-line); background: var(--k-paper);
}
.isle .head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.isle .k { margin: 0; font: 600 13.5px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink); }
.isle .b { margin: 8px 0 0; font: 400 11.5px/1.4 var(--ion-font-family, system-ui); color: var(--k-mute); }
.isle .quiet { margin: 0; }
.isle .quiet.warn { color: var(--k-warn); }
.isle .fields { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 9px; }
.isle label { display: flex; flex-direction: column; gap: 5px; }
.isle .which { font: 400 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-mute); }
.isle .field {
  display: flex; align-items: center; gap: 5px;
  border: 1px solid var(--k-line); border-radius: 9px; background: var(--k-paper);
  padding: 0 9px; min-height: 44px;
}
.isle .field input {
  width: 100%; border: 0; background: none; outline: none; padding: 0;
  font: 500 15px/1 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.isle .field i { font: 400 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-mute); font-style: normal; }
.isle .spin { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
.isle .spin button:not(.flip) {
  width: 44px; height: 44px; display: grid; place-items: center; cursor: pointer;
  border: 1px solid var(--k-line); border-radius: 10px; background: var(--k-paper); color: var(--k-dim);
}
.isle .spin button:not(.flip):active { background: var(--k-fill); }
.isle .deg {
  min-width: 46px; text-align: center;
  font: 500 14px/1 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.isle .spin .flip { margin-left: auto; }

.found {
  margin: 0 0 12px; padding: 12px 13px; border-radius: 12px;
  background: color-mix(in oklab, var(--k-accent) 9%, var(--k-paper));
  border: 1px solid color-mix(in oklab, var(--k-accent) 28%, transparent);
}
.found .k { margin: 0 0 5px; font: 600 14px/1.2 var(--ion-font-family, system-ui); color: var(--k-ink); }
.found .b { margin: 0 0 5px; font: 400 12px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim); }
.found .row { display: flex; align-items: center; gap: 12px; }
.found .saw { color: var(--k-ink); font-weight: 500; }
.found .design { flex: 1; margin-top: 8px; padding: 12px; min-height: 44px; font-size: 13.5px; }
.found .quiet { margin: 8px 0 0; }
.found .services {
  margin: 4px 0 2px; padding: 10px 11px; border-radius: 10px;
  background: var(--k-paper); border: 1px solid color-mix(in oklab, var(--k-accent) 20%, transparent);
}
.found .lock-lede { margin: 0 0 8px; }
.found .lockrow {
  display: flex; align-items: center; gap: 9px; padding: 6px 0;
  font: 500 13px/1.3 var(--ion-font-family, system-ui); color: var(--k-ink);
}
.found .lockrow input { width: 18px; height: 18px; accent-color: var(--k-accent); flex: 0 0 auto; }
.found .fronts {
  margin: 4px 0 2px; padding: 10px 11px; border-radius: 10px;
  background: var(--k-paper); border: 1px solid color-mix(in oklab, var(--k-accent) 20%, transparent);
}
.found .wallshot {
  display: flex; align-items: center; gap: 10px; width: 100%; margin: 6px 0 0;
  padding: 10px 11px; min-height: 44px; border: 0; border-radius: 10px;
  background: var(--k-fill); color: var(--k-ink); text-align: left; cursor: pointer;
}
.found .wallshot:disabled { opacity: 0.5; }
.found .wallshot .text { display: grid; gap: 2px; min-width: 0; }
.found .wallshot .k { margin: 0; font: 600 13px/1.2 var(--ion-font-family, system-ui); }
.found .wallshot .b { margin: 0; font: 400 11.5px/1.35 var(--ion-font-family, system-ui); color: var(--k-dim); }

.findings {
  list-style: none; margin: 4px 0 12px; padding: 0;
  display: grid; gap: 6px;
}
.findings li {
  display: grid; gap: 4px; padding: 10px 12px; border-radius: 11px;
  background: var(--k-fill); cursor: pointer;
}
.findings li.fault { background: color-mix(in oklab, var(--k-warn) 9%, var(--k-paper)); }
.findings .tag {
  font: 500 10.5px/1 var(--ion-font-family, system-ui);
  letter-spacing: .05em; text-transform: uppercase; color: var(--k-mute);
}
.findings li.fault .tag { color: var(--k-warn); }
.findings .what { font: 400 13px/1.45 var(--ion-font-family, system-ui); color: var(--k-dim); }
.findings .acts { display: flex; align-items: center; gap: 10px; margin-top: 2px; }
.findings .fix {
  border: 0; border-radius: 8px; cursor: pointer; padding: 8px 11px; min-height: 32px;
  background: var(--k-ink); color: var(--k-paper); font: 600 12px/1 var(--ion-font-family, system-ui);
}
.findings .show { margin-left: auto; display: inline-flex; align-items: center; gap: 3px; font: 500 11.5px/1 var(--ion-font-family, system-ui); color: var(--k-accent); }
.findings li:not(.goes) { cursor: default; }
.lede .est { color: var(--k-accent); }

/* ── a wide screen: the sheet stands beside the kitchen, not on it ─────────────── */
@media (min-width: 900px) {
  .planner { --panel-w: 420px; }
  .stage, :deep(.plan) { right: var(--panel-w); width: auto; }
  .brandbar { right: var(--panel-w); }
  .hint { left: calc((100% - var(--panel-w)) / 2); }
  .sheet {
    left: auto; width: var(--panel-w); top: 0; max-height: none; border-radius: 0;
    box-shadow: -1px 0 0 var(--k-line); transform: none !important; padding-top: 6px;
  }
  .sheet .grab { display: none; }
  .bar { left: auto; width: var(--panel-w); box-shadow: -1px 0 0 var(--k-line), 0 -1px 0 var(--k-line); }
  .askbtn { right: calc(var(--panel-w) + 14px); }
}
</style>
